import React, { useState, useEffect, useRef } from "react";
import Shimmer from "../../components/Shimmer";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Image,
  Checkbox,
  Button,
  Divider,
  Badge,
  Heading,
  Stack,
  useToast,
  Input,
  Select,
  InputGroup,
  InputLeftAddon,
  InputLeftElement,
  Grid,
  FormLabel,
  FormControl,
  FormErrorMessage,
  IconButton,
  Collapse,
  Radio,
  RadioGroup,
  GridItem,
  SimpleGrid,
} from "@chakra-ui/react";
import { FaRegUser } from "react-icons/fa";
import { BsSuitcase } from "react-icons/bs";
import { FiTrash2, FiPlus } from "react-icons/fi";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";

import { useNavigate } from "react-router-dom";
import axios from "axios";
import InputWithSuggestionSimple from "../../components/InputWithSuggestionSimple";
import RouteFlow from "../../components/RouteFlow";
import { useLocation } from "react-router-dom";
import { pick } from "highcharts";
import { normalizeTags } from "../../utils/normalizeTags";

const BASE_URL = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

export default function Booking() {
  const [specificPickupLocation, setSpecificPickupLocation] = useState("");
  const [specificDropLocation, setSpecificDropLocation] = useState("");
  const [roofCarrier, setRoofCarrier] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();
  const isCustomBooking = location.state?.isCustomBooking || false;
  const [customFare, setCustomFare] = useState(null);
  const [globalSettings, setGlobalSettings] = useState({
    customCommission: "",
    tollTaxPerKm: "",
  });
  const [calculatingFare, setCalculatingFare] = useState(false);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [pricingType, setPricingType] = useState("fixed");
  const [customInclusionChecks, setCustomInclusionChecks] = useState({
    tolls: false,
    stateTaxes: false,
    parkingFees: false,
    entryFees: false,
  });
  const [customExclusions, setCustomExclusions] = useState([]);
  const [customInclusions, setCustomInclusions] = useState([]);
  const [error, setError] = useState("");
  const [leadId, setLeadId] = useState(null);
  const [finalTotal, setFinalTotal] = useState(0);
  const [tokenAmount, setTokenAmount] = useState(0);
  const [manualDiscount, setManualDiscount] = useState(0);
  const tokenRef = useRef(null);
  const [fareBreakup, setFareBreakup] = useState(null);
  const [sendWhatsappMsg, setSendWhatsappMsg] = useState(false);
  const fieldRefs = useRef({});

  // Search and Customer Form States
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: "",
    email: "",
    mobile: "",
    whatsapp: "",
    city: "",
    gender: "",
    language: "",
  });
  const [tripNotes, setTripNotes] = useState("");
  const [passengers, setPassengers] = useState([
    { name: "", mobile: "", whatsapp: "" },
  ]);
  const [errors, setErrors] = useState({});
  const [passengerErrors, setPassengerErrors] = useState([{}]);
  const [whatsappSameAsMobile, setWhatsappSameAsMobile] = useState(true);
  const [passengerWhatsappSameAsMobile, setPassengerWhatsappSameAsMobile] =
    useState([false]);
  const [firstPassengerSameAsCustomer, setFirstPassengerSameAsCustomer] =
    useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isCorporate, setIsCorporate] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  const [corporateDetails, setCorporateDetails] = useState({
    companyName: "",
    gstNo: "",
    corporateId: null,
  });

  const [formData, setFormData] = useState({
    fixedRate: "",
    ratePerKm: "",
    extraKmRate: "",
    driverAllowancePerDay: "",
    waitingCharge: "",
    nightCharges: "",
    minimumKmPerDay: "",
  });


  useEffect(() => {
    if (!isCustomBooking) return;

    const fetchGlobalSettings = async () => {
      try {
        const token = localStorage.getItem("prima_session");
        const res = await axios.get(`${BASE_URL}/api/global-fields`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true,
        });

        const fetchedSettings = {
          customCommission: String(res?.data?.data?.globalCommission ?? ""),
          tollTaxPerKm: String(res?.data?.data?.globalTollTax ?? ""),
        };

        setGlobalSettings(fetchedSettings);
      } catch (err) {
        console.error("Error fetching global custom booking settings:", err);
      }
    };

    fetchGlobalSettings();
  }, [isCustomBooking]);

  const [formErrors, setFormErrors] = useState({});

  const searchTimeoutRef = useRef(null);

  const tripKey =
    bookingData?.tripType === "RT"
      ? "outstationRoundTrip"
      : bookingData?.tripType === "OW"
        ? "outstationOneWay"
        : bookingData?.tripType === "AT"
          ? "airportTransfer"
          : bookingData?.tripType === "LR"
            ? "localRental"
            : null;

  const resolveTripPricing = (pricingObj, key) => {
    if (!pricingObj || !key) return null;
    const raw = pricingObj[key];
    return Array.isArray(raw) ? raw[0] : raw;
  };

  const resolvedTripPricing =
    resolveTripPricing(bookingData?.pricing, tripKey) ||
    resolveTripPricing(bookingData?.carDetails?.pricing, tripKey) ||
    resolveTripPricing(vehicle?.pricing, tripKey) ||
    null;

  const waitingCharge =
    resolvedTripPricing?.waitingCharge
      ? Number(resolvedTripPricing.waitingCharge)
      : 0;

  const calculateCustomFare = async () => {
    if (!bookingData) return;

    if (!validatePricingInputs()) {
      return;
    }

    try {
      setCalculatingFare(true);

      const token = localStorage.getItem("prima_session");

      const payload = {
        vehicleSegment: bookingData.vehicleSegmentId,
        pickupLocation:
          typeof bookingData.from === "object"
            ? bookingData.from?.address || bookingData.from
            : bookingData.from,
        dropLocation:
          typeof bookingData.to === "object"
            ? bookingData.to?.address || bookingData.to
            : bookingData.to,
        pickupDateTime: parsePickupDateTime(
          bookingData.pickupdate,
          bookingData.pickuptime,
        ),
        returnDateTime: parseReturnDate(bookingData.returndate),
        pricingType,
        pricingInputs: {
          isTollIncludedInPrice: !!customInclusionChecks.tolls,
        },
      };

      if (pricingType === "fixed") {
        payload.pricingInputs.baseRate = Number(formData.fixedRate);
        payload.pricingInputs.distance = Number(bookingData.distance || 0);
        if (globalSettings.customCommission) {
          payload.pricingInputs.globalCommission = Number(globalSettings.customCommission || 0);
        }
      } else {
        payload.pricingInputs.perKmRate = Number(formData.ratePerKm);
        payload.pricingInputs.extraKmRate = Number(formData.extraKmRate || 0);
        payload.pricingInputs.driverAllowance = Number(
          formData.driverAllowancePerDay,
        );
        payload.pricingInputs.waitingCharge = Number(formData.waitingCharge || 0);
        payload.pricingInputs.nightCharges = Number(formData.nightCharges || 0);
        payload.pricingInputs.tollTax = Number(globalSettings.tollTaxPerKm || 0);
        payload.pricingInputs.minimumKmPerDay = Number(formData.minimumKmPerDay || 0);
        payload.pricingInputs.distance = Number(bookingData.distance || 0);
        if (globalSettings.customCommission) {
          payload.pricingInputs.globalCommission = Number(globalSettings.customCommission || 0);
        }
      }

      const res = await axios.post(
        `${BASE_URL}/management/custom-booking/calculate-fare`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (res.data?.data) {
        setCustomFare(res.data.data);
        setFareBreakup(res.data.data.pricingOutputs);
        setFinalTotal(res.data.data.pricingOutputs.totalFare);
      }
    } catch (err) {
      toast({
        title: "Custom fare calculation failed",
        status: "error",
      });
    } finally {
      setCalculatingFare(false);
    }
  };

  // validation checks for phone whatsapp and email
  const normalizeIndianMobile = (value) => {
    if (!value) return "";
    // Remove all non-digits
    let digits = value.replace(/\D/g, "");
    // Remove country code if present
    if (digits.startsWith("91") && digits.length === 12) {
      digits = digits.slice(2);
    }
    return digits;
  };

  const isValidMobile = (value) => {
    const mobile = normalizeIndianMobile(value);
    return /^[6-9]\d{9}$/.test(mobile);
  };

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const isValidLanguage = (value) => {
    // No numerics allowed
    return /^[a-zA-Z\s,]*$/.test(value);
  };

  const isValidGSTIN = (value) => {
    return /^[0-9A-Z]{15}$/.test(value);
  };

  // Handle form input changes for pricing details
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => {
      const updated = { ...p, [name]: value };
      return updated;
    });

    // Clear error for this field when user starts typing/changing
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleCalculateFareClick = () => {
    const inclusions = ["Fuel", "Driver allowance"];
    const exclusions = [];

    if (customInclusionChecks.tolls) inclusions.push("Tolls");
    else exclusions.push("Tolls");

    if (customInclusionChecks.stateTaxes) inclusions.push("State taxes");
    else exclusions.push("State taxes");

    if (customInclusionChecks.parkingFees) inclusions.push("Parking Fees");
    else exclusions.push("Parking Fees");

    if (customInclusionChecks.entryFees) inclusions.push("Entry Fees");
    else exclusions.push("Entry Fees");

    // save in state
    setCustomInclusions(inclusions);
    setCustomExclusions(exclusions);
    // call original function
    calculateCustomFare();
  };
  // Get pricing inclusions and exclusions
  const getPricingInclusions = () => {
    // Handle custom bookings
    if (isCustomBooking) {
      const driverAllowance = Number(formData.driverAllowancePerDay || 0);
      const mileageWithoutDriverAllowance =
        pricingType === "mileage" && driverAllowance <= 0;
      const includes = [...customInclusions].filter((item) => {
        const normalizedItem = String(item || "").trim().toLowerCase();
        return !(mileageWithoutDriverAllowance && normalizedItem === "driver allowance");
      });
      const excludes = [...customExclusions];

      if (includes.length === 0 && excludes.length === 0) {
        const fallbackIncludes = ["Fuel", "Driver allowance"];
        const fallbackExcludes = [];

        // For mileage-based pricing, conditionally handle driver
        if (mileageWithoutDriverAllowance) {
          fallbackIncludes.splice(fallbackIncludes.indexOf("Driver allowance"), 1);
          fallbackExcludes.push("Driver");
        } else if (pricingType === "fixed") {
          // For fixed pricing, keep driver allowance in includes and do not add driver to excludes
        }

        return {
          includes: fallbackIncludes,
          excludes: [...fallbackExcludes, "Any permissions or fees related to trip"],
        };
      }

      return {
        includes: includes.includes("Fuel") ? includes : [...includes, "Fuel"],
        excludes: [
          ...excludes,
          ...(mileageWithoutDriverAllowance ? ["Driver"] : []),
          "Any permissions or fees related to trip",
        ],
      };
    }

    // Original logic for non-custom bookings
    if (!vehicle || !bookingData) return { includes: [], excludes: [] };

    const tripKey =
      bookingData.tripType === "RT"
        ? "outstationRoundTrip"
        : bookingData.tripType === "OW"
          ? "outstationOneWay"
          : bookingData.tripType === "AT"
            ? "airportTransfer"
            : null;

    if (!tripKey) return { includes: [], excludes: [] };

    const pricing = vehicle.pricing?.[tripKey];

    // For Outstation One Way, check if there are stops
    if (bookingData.tripType === "OW") {
      const hasStops = bookingData.stops && bookingData.stops.length > 0;

      if (hasStops && pricing?.includesWithStops) {
        // Use the "with stops" includes/excludes if available
        return {
          includes: [...(pricing.includesWithStops || [])],
          excludes: [...(pricing.excludesWithStops || [])],
        };
      } else {
        // Use the default includes/excludes (no stops)
        return {
          includes: [...(pricing.includes || [])],
          excludes: [...(pricing.excludes || [])],
        };
      }
    }

    // For other trip types, use default includes/excludes
    let includes = [...(pricing?.includes || [])];
    let excludes = [...(pricing?.excludes || [])];

    return {
      includes,
      excludes,
    };
  };

  // Search Leads/Customers Function
  const searchLeads = async (query) => {
    if (!query || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsSearching(true);
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) {
        toast({
          title: "Authentication required",
          status: "error",
          duration: 3000,
        });
        return;
      }

      const queryText = query.trim();
      const queryLower = queryText.toLowerCase();
      // Check if query is a number (mobile) or text (name/email)
      const isNumeric = /^\d+$/.test(queryText);
      const searchParam = isNumeric ? `mobile=${queryText}` : `name=${queryText}`;

      const [customerResponse, customersListResponse] = await Promise.all([
        axios.get(`${BASE_URL}/management/customer?${searchParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/management/customers?page=1&limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const directCustomers =
        customerResponse.data?.success && customerResponse.data?.data
          ? Array.isArray(customerResponse.data.data)
            ? customerResponse.data.data
            : [customerResponse.data.data]
          : [];

      const listPayload = customersListResponse.data?.data;
      const allCustomers = Array.isArray(listPayload)
        ? listPayload
        : Array.isArray(listPayload?.data)
          ? listPayload.data
          : Array.isArray(customersListResponse.data?.customers)
            ? customersListResponse.data.customers
            : [];

      const partialCustomers = allCustomers.filter((customer) => {
        const name = String(customer?.name || "").toLowerCase();
        const email = String(customer?.email || "").toLowerCase();
        const mobile = String(customer?.mobile || "").toLowerCase();
        const whatsapp = String(customer?.whatsapp || "").toLowerCase();
        return (
          name.includes(queryLower) ||
          email.includes(queryLower) ||
          mobile.includes(queryLower) ||
          whatsapp.includes(queryLower)
        );
      });

      const uniqueCustomers = new Map();
      [...directCustomers, ...partialCustomers].forEach((customer) => {
        const key =
          customer?._id ||
          customer?.customerId ||
          `${customer?.mobile || ""}-${customer?.name || ""}`;
        if (key) uniqueCustomers.set(key, customer);
      });

      const customers = Array.from(uniqueCustomers.values());
      console.log("Search results:", customers);
      setSearchResults(customers);
      setShowSearchDropdown(customers.length > 0);
    } catch (error) {
      console.error("Error searching customers:", error);
      // Don't show error toast for no results, just clear the dropdown
      if (error.response?.status !== 404) {
        toast({
          title: "Search failed",
          description:
            error.response?.data?.message || "Could not search customers",
          status: "error",
          duration: 3000,
        });
      }
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchLeads(value);
    }, 300);
  };

  // Handle selecting a lead/customer from search
  const handleLeadSelect = (lead) => {
    setCustomerForm({
      customerId: lead._id || lead.customerId,
      name: lead.name || "",
      mobile: normalizeIndianMobile(lead.mobile) || "",
      email: lead.email || "",
      whatsapp:
        normalizeIndianMobile(lead.whatsapp) ||
        normalizeIndianMobile(lead.mobile) ||
        "",
      city: lead.city || "",
      gender: lead.gender || "",
      language: lead.language || "",
    });
    setSearchQuery(lead.name || lead.mobile || "");
    setShowSearchDropdown(false);
    setSearchResults([]);
    setIsEditingCustomer(false);

    // Update customer state with customer ID
    setCustomer({
      customerId: lead._id || lead.customerId,
      name: lead.name,
      mobile: normalizeIndianMobile(lead.mobile),
      email: lead.email,
      whatsapp:
        normalizeIndianMobile(lead.whatsapp) ||
        normalizeIndianMobile(lead.mobile),
      city: lead.city,
      gender: lead.gender,
    });

    // ✅ CORPORATE HYDRATION FROM SEARCH RESULT
    if (lead.isCorporate && lead.corporateDetails) {
      setIsCorporate(true);
      setCorporateDetails({
        companyName: lead.corporateDetails.companyName || "",
        gstNo: lead.corporateDetails.gstNo || "",
        corporateId: lead.corporateDetails._id || null,
      });
    } else {
      setIsCorporate(false);
      setCorporateDetails({
        companyName: "",
        gstNo: "",
        corporateId: null,
      });
    }


    // Update first passenger if checkbox is checked
    if (firstPassengerSameAsCustomer) {
      setPassengers((prev) => {
        const updated = [...prev];
        updated[0] = {
          name: lead.name || "",
          mobile: normalizeIndianMobile(lead.mobile) || "",
          whatsapp:
            normalizeIndianMobile(lead.whatsapp) ||
            normalizeIndianMobile(lead.mobile) ||
            "",
        };
        return updated;
      });
    }

    toast({
      title: "Lead selected",
      description: `Selected ${lead.name}`,
      status: "success",
      duration: 2000,
    });
  };

  // Update customer form handler
  const handleCustomerFormChange = (field, value) => {
    let updatedValue = value;

    // normalize phone fields ONLY
    if (field === "mobile" || field === "whatsapp") {
      updatedValue = normalizeIndianMobile(value);
    }

    setCustomerForm((prev) => ({
      ...prev,
      [field]: updatedValue,
      // auto sync whatsapp ONLY if checkbox is ON
      ...(field === "mobile" && whatsappSameAsMobile
        ? { whatsapp: updatedValue }
        : {}),
    }));

    setIsEditingCustomer(true);

    // clear field error while typing
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });

    if (field === "mobile" && whatsappSameAsMobile) {
      setErrors((prev) => {
        if (!prev.whatsapp) return prev;
        const copy = { ...prev };
        delete copy.whatsapp;
        return copy;
      });
    }
  };

  // Passenger Management
  const handlePassengerChange = (index, field, value) => {
    const newPassengers = [...passengers];
    let updatedValue = value;
    if (field === "mobile" || field === "whatsapp") {
      updatedValue = normalizeIndianMobile(value);
    }
    newPassengers[index][field] = updatedValue;
    if (field === "mobile" && passengerWhatsappSameAsMobile[index]) {
      newPassengers[index].whatsapp = updatedValue;
    }
    setPassengers(newPassengers);
    const newErrors = [...passengerErrors];
    if (newErrors[index]) delete newErrors[index][field];
    setPassengerErrors(newErrors);
  };

  const maxPassengerCapacity = Number(vehicle?.passengerCapacity) || 0;

  const addPassenger = () => {
    // Validate existing passengers before allowing new ones
    const hasErrors = passengers.some((p, index) => {
      if (index === 0 && firstPassengerSameAsCustomer) {
        return false;
      }
      return !p.name.trim() || !p.mobile.trim() || !p.whatsapp.trim() ||
        !isValidMobile(p.mobile) || !isValidMobile(p.whatsapp);
    });

    if (hasErrors) {
      toast({
        title: "Complete passenger details",
        description: "Please fill in all required fields for existing passengers before adding a new one.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const capacity = Number(vehicle?.passengerCapacity) || 0;
    // If capacity not available, allow fallback (optional)
    if (!capacity) {
      setPassengers([...passengers, { name: "", mobile: "", whatsapp: "" }]);
      setPassengerErrors([...passengerErrors, {}]);
      setPassengerWhatsappSameAsMobile([...passengerWhatsappSameAsMobile, false]);
      return;
    }
    // Capacity validation
    if (passengers.length >= capacity) {
      toast({
        title: "Passenger limit reached",
        description: `This vehicle allows maximum ${capacity} passengers.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    //  Add passenger
    setPassengers([...passengers, { name: "", mobile: "", whatsapp: "" }]);
    setPassengerErrors([...passengerErrors, {}]);
    setPassengerWhatsappSameAsMobile([...passengerWhatsappSameAsMobile, false]);
  };

  const removePassenger = (index) => {
    if (passengers.length === 1) {
      toast({
        title: "Cannot remove",
        description: "At least one passenger is required",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // If removing passenger 1 (index 0), uncheck "same as booked customer"
    if (index === 0) {
      setFirstPassengerSameAsCustomer(false);
    }

    const newPassengers = passengers.filter((_, i) => i !== index);
    const newErrors = passengerErrors.filter((_, i) => i !== index);
    const newCheckboxStates = passengerWhatsappSameAsMobile.filter(
      (_, i) => i !== index,
    );
    setPassengers(newPassengers);
    setPassengerErrors(newErrors);
    setPassengerWhatsappSameAsMobile(newCheckboxStates);
  };

  // Save customer changes
  const saveCustomerChanges = () => {
    setCustomer({
      ...customer,
      ...customerForm,
    });
    setIsEditingCustomer(false);
    toast({
      title: "Customer updated",
      status: "success",
      duration: 2000,
    });
  };

  // Sync whatsapp with mobile for customer
  useEffect(() => {
    if (!whatsappSameAsMobile) return;
    setCustomerForm((prev) => ({
      ...prev,
      whatsapp: prev.mobile,
    }));
    setErrors((prev) => {
      if (!prev.whatsapp) return prev;
      const copy = { ...prev };
      delete copy.whatsapp;
      return copy;
    });
  }, [customerForm.mobile, whatsappSameAsMobile]);

  // ✅ FIX: Keep first passenger in sync with customerForm whenever
  // "same as booked customer" is checked. This handles the case where
  // the checkbox is ticked before (or while) the customer form is being filled,
  // ensuring the passenger is never saved with stale/empty values.
  useEffect(() => {
    if (!firstPassengerSameAsCustomer) return;

    setPassengers((prev) => {
      const updated = [...prev];
      updated[0] = {
        name: customerForm.name || "",
        mobile: customerForm.mobile || "",
        whatsapp: customerForm.whatsapp || customerForm.mobile || "",
      };
      return updated;
    });

    // Clear any validation errors on the first passenger when syncing
    setPassengerErrors((prev) => {
      const updated = [...prev];
      updated[0] = {};
      return updated;
    });
  }, [
    firstPassengerSameAsCustomer,
    customerForm.name,
    customerForm.mobile,
    customerForm.whatsapp,
  ]);

  // Clear corporate details if not corporate
  useEffect(() => {
    if (!isCorporate) {
      setCorporateDetails({
        companyName: "",
        gstNo: "",
        corporateId: null,
      });
    }
  }, [isCorporate]);

  // (manualDiscount replaces previous negotiated/priceMode logic)
  useEffect(() => {
    const el = tokenRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [tokenRef.current]);

  // Auto calculate token = 15% of discounted final price
  useEffect(() => {
    const baseFare =
      isCustomBooking && customFare
        ? Number(customFare.pricingOutputs?.totalFare || 0)
        : Number(finalTotal || 0);

    const discountedFinal = Math.max(0, baseFare - Number(manualDiscount || 0));
    const defaultToken = discountedFinal * 0.15; // 15%
    const tokenValue = defaultToken.toFixed(2);
    setTokenAmount(tokenValue === '0.00' ? 0 : tokenValue);
  }, [finalTotal, manualDiscount, customFare, isCustomBooking]);

  const systemCalculated =
    isCustomBooking && customFare
      ? Number(customFare.pricingOutputs?.totalFare || 0)
      : Number(finalTotal || 0);

  // Final price after manual discount input
  const finalPrice = Math.max(
    0,
    systemCalculated - Number(manualDiscount || 0),
  );
  const isOverDiscount =
    Number(manualDiscount || 0) > Number(systemCalculated || 0);
  const isTokenOverFinalPrice =
    Number(tokenAmount || 0) > Number(finalPrice || 0);

  const computePackageFare = (booking) => {
    if (!booking?.packageId) return 0;
    const amountWithoutStay = Number(booking.amountWithoutStay) || 0;
    const amountWithStay = Number(booking.amountWithStay) || 0;
    return amountWithStay > 0 ? amountWithStay : amountWithoutStay;
  };

  // -------------------------
  // Format time in 12-hour format
  // -------------------------
  const formatTime12 = (timeStr) => {
    if (!timeStr) return "";
    let [hours, minutes] = timeStr.split(":").map(Number);
    let modifier = "AM";

    if (hours >= 12) {
      modifier = "PM";
      if (hours > 12) hours -= 12;
    }
    if (hours === 0) hours = 12;

    return `${hours}:${minutes.toString().padStart(2, "0")} ${modifier}`;
  };

  // -------------------------
  // Calculate Distance using Google Maps API
  // -------------------------
  const calculateDistanceAPI = async (from, to, tripType, stops = []) => {
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("User not logged in");

      // Filter out empty stops - stops are already strings from autocomplete
      const validStops = Array.isArray(stops)
        ? stops
          .filter(stop => {
            const stopStr = typeof stop === "string" ? stop : (stop?.name || stop?.description || "");
            return stopStr && stopStr.trim();
          })
          .map(stop => typeof stop === "string" ? stop : (stop?.name || stop?.description || ""))
        : [];

      const payload = {
        from: typeof from === "object" ? from?.address || from : from,
        to: typeof to === "object" ? to?.address || to : to,
        tripType,
        stops: validStops,
      };

      console.log("🔍 Calculating distance with payload:", payload);

      const res = await axios.post(
        `${BASE_URL}/management/calculate-distance`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      console.log("✅ Distance API response:", res.data);

      if (res.data?.success && res.data?.data) {
        return {
          distance: Number(res.data.data.distance) || 0,
          distanceText: res.data.data.distanceText || "",
          duration: Number(res.data.data.duration) || 0,
          durationText: res.data.data.durationText || "",
        };
      }
      return {
        distance: 0,
        distanceText: "",
        duration: 0,
        durationText: "",
      };
    } catch (err) {
      console.error(
        "Error calculating distance:",
        err.response?.data || err.message,
      );
      toast({
        title: "Distance Calculation Warning",
        description:
          err.response?.data?.message ||
          "Could not calculate distance. Using default value.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return {
        distance: 0,
        distanceText: "",
        duration: 0,
        durationText: "",
      };
    }
  };

  // -------------------------
  // Parse pickup date + time for backend
  // -------------------------
  const parsePickupDateTime = (dateStr, timeStr) => {
    if (!dateStr) dateStr = "1970-01-01";
    if (!timeStr) timeStr = "00:00";

    let year, month, day;

    if (dateStr.includes("/")) {
      [day, month, year] = dateStr.split("/");
    } else if (dateStr.includes("-")) {
      [year, month, day] = dateStr.split("-");
    } else {
      console.error("Invalid date format:", dateStr);
      return new Date().toISOString();
    }

    const [hours, minutes] = timeStr.split(":").map(Number);

    // Create date in IST timezone
    const dt = new Date(
      parseInt(year),
      parseInt(month) - 1, // Month is 0-indexed
      parseInt(day),
      hours,
      minutes,
      0,
      0,
    );

    if (isNaN(dt.getTime())) {
      console.error("Invalid date created:", {
        dateStr,
        timeStr,
        year,
        month,
        day,
        hours,
        minutes,
      });
      return new Date().toISOString();
    }

    return dt.toISOString();
  };

  // -------------------------
  // Parse return date for backend (converts DD/MM/YYYY to ISO 8601)
  // -------------------------
  const parseReturnDate = (dateStr) => {
    if (!dateStr) return null;

    let year, month, day;

    if (dateStr.includes("/")) {
      [day, month, year] = dateStr.split("/");
    } else if (dateStr.includes("-")) {
      [year, month, day] = dateStr.split("-");
    } else {
      console.error("Invalid date format:", dateStr);
      return null;
    }

    // Create date with end-of-day time (20:00 IST)
    const dt = new Date(
      parseInt(year),
      parseInt(month) - 1, // Month is 0-indexed
      parseInt(day),
      20, // 20:00 (8 PM)
      0,
      0,
      0,
    );

    if (isNaN(dt.getTime())) {
      console.error("Invalid return date created:", {
        dateStr,
        year,
        month,
        day,
      });
      return null;
    }

    return dt.toISOString();
  };

  // -------------------------
  // Fetch fare from backend
  // -------------------------
  const fetchFareFromBackend = async (bookingData, token) => {
    try {
      const payload = {
        from:
          typeof bookingData.from === "object"
            ? bookingData.from?.address || bookingData.from
            : bookingData.from,
        to:
          typeof bookingData.to === "object"
            ? bookingData.to?.address || bookingData.to
            : bookingData.to,
        tripType: bookingData.tripType,
        pickupDate: bookingData.pickupdate,
        pickupTime: bookingData.pickuptime,
        returnDate: bookingData.returndate,
        distance: bookingData.distance || 0,
        duration: bookingData.duration || 0,
        durationText: bookingData.durationText || "",
        stops: bookingData.stops || [],
      };

      // Add hours and distance for Local Rental
      if (bookingData.tripType === "LR") {
        payload.localRentalTime = Number(bookingData.hours) || 4;
        payload.localRentalDistance = Number(bookingData.distance) || 40;
      }

      const res = await axios.post(`${BASE_URL}/management/find`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success && res.data?.data) {
        const segmentFare = res.data.data.find(
          (item) => item.id === bookingData.vehicleSegmentId,
        );
        console.log("SELECTED SEGMENT FARE (frontend):", segmentFare);
        setFareBreakup(segmentFare);
        setFinalTotal(Number(segmentFare.customerPayable) || 0);
        return 0;
      }
      return fareBreakup?.customerPayable || 0;
    } catch (err) {
      console.error("Error fetching fare from backend:", err);
      return 0;
    }
  };

  // -------------------------
  // Load customer + booking + vehicle
  // -------------------------
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);

        const customerLS = localStorage.getItem("customerDetails");
        const bookingLS = localStorage.getItem("lead_booking_data");

        if (!bookingLS) {
          setError("Booking data missing");
          toast({
            title: "Missing data",
            description:
              "Booking information not found. Please go back and select a vehicle.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const customerData = customerLS ? JSON.parse(customerLS) : null;
        const booking = JSON.parse(bookingLS);

        // Set customer if exists, otherwise leave as null for user to search/create
        if (customerData) {
          setCustomer(customerData);
          // Initialize customer form with existing data
          setCustomerForm({
            name: customerData.name || "",
            mobile: customerData.mobile || "",
            email: customerData.email || "",
            whatsapp: customerData.whatsapp || customerData.mobile || "",
            city: customerData.city || "",
            gender: customerData.gender || "",
            language: customerData.language || "",
          });
          // Initialize first passenger
          setPassengers([
            {
              name: customerData.name || "",
              mobile: customerData.mobile || "",
              whatsapp: customerData.whatsapp || customerData.mobile || "",
            },
          ]);
        } else {
          // Start with empty form for new customer
          setCustomer({ customerId: null });
          setCustomerForm({
            name: "",
            mobile: "",
            email: "",
            whatsapp: "",
            city: "",
            gender: "",
            language: "",
          });
          setPassengers([{ name: "", mobile: "", whatsapp: "" }]);
        }

        setTripNotes(booking.notes || booking.comments || "");

        // Calculate distance using Google Maps API if distance is 0 or missing
        if (!booking.distance || booking.distance === 0) {
          console.log(
            "Distance is 0 or missing, calculating from Google Maps...",
          );
          const calculatedDistanceData = await calculateDistanceAPI(
            booking.from,
            booking.to,
            booking.tripType,
            booking.stops || [],
          );

          if (calculatedDistanceData.distance > 0) {
            booking.distance = calculatedDistanceData.distance;
            booking.duration = calculatedDistanceData.duration;
            booking.durationText = calculatedDistanceData.durationText;
            // console.log(`✅ Distance calculated: ${calculatedDistanceData.distance} km`);

            // Update localStorage with new distance
            localStorage.setItem("lead_booking_data", JSON.stringify(booking));

            toast({
              title: "Distance Calculated",
              description: `Distance: ${calculatedDistanceData.distance} km`,
              status: "success",
              duration: 2000,
              isClosable: true,
            });
          }
        }

        console.log("Loaded booking data:", booking);
        setBookingData(booking);

        // Only create passenger entry if customer data exists
        if (customerData) {
          const passenger = {
            name: customerData.name,
            mobile: customerData.mobile,
            whatsapp: customerData.whatsapp,
          };
          localStorage.setItem("lead_passengers", JSON.stringify([passenger]));
        } else {
          // Initialize empty passenger array
          localStorage.setItem("lead_passengers", JSON.stringify([]));
        }

        if (booking.packageId) {
          const packageFare = Number(booking.selectedAmount) || 0;
          const gstAmount = Number(booking?.gstOnCommission || booking?.gst || 0);

          const totalWithGST = packageFare + gstAmount;

          setVehicle(null);

          setFareBreakup({
            baseFare: packageFare,
            customerPayable: totalWithGST,
            originalFare: totalWithGST,
            discountAmount: 0,
            discountPercent: 0,
            tollTax: 0,
            nightCharge: 0,
            gstOnCommission: gstAmount,
          });

          setFinalTotal(totalWithGST);

          return;
        }

        const token = localStorage.getItem("prima_session");
        if (!token) {
          setError("User not logged in");
          toast({
            title: "Error",
            description: "User token not found. Please login again.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const res = await axios.get(
          `${BASE_URL}/management/vehicle-segment/${booking.vehicleSegmentId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        const vehicleData = res.data?.data || res.data;
        console.log("Vehicle data from backend:", vehicleData);
        // console.log('Vehicle inclusions:', vehicleData.pricing.outstationOneWay.includes);
        // console.log('Vehicle exclusions:', vehicleData.pricing.outstationOneWay.excludes);
        setVehicle(vehicleData);

        // Fare will be calculated by the useEffect that watches bookingData.distance
        // console.log('Vehicle data loaded, waiting for fare recalculation...');

        // Optionally fetch backend fare for logging
        const backendFare = await fetchFareFromBackend(booking, token);
        // console.log('Backend fare:', backendFare);
      } catch (err) {
        console.error("Error fetching booking details:", err);
        setError(err.message || "Failed to load data");
        toast({
          title: "Error",
          description: err.response?.data?.message || err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [toast]);

  const validateCustomerForm = () => {
    const newErrors = {};

    if (!customerForm.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (customerForm.email.trim() && !isValidEmail(customerForm.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!customerForm.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!isValidMobile(customerForm.mobile)) {
      newErrors.mobile = "Enter valid 10-digit mobile number";
    }

    if (!customerForm.whatsapp.trim()) {
      newErrors.whatsapp = "Whatsapp number is required";
    } else if (!isValidMobile(customerForm.whatsapp)) {
      newErrors.whatsapp = "Enter valid 10-digit Whatsapp number";
    }

    if (!customerForm.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!customerForm.gender.trim()) {
      newErrors.gender = "Gender is required";
    }
    if (
      customerForm.language.trim() &&
      !isValidLanguage(customerForm.language)
    ) {
      newErrors.language = "Language field cannot contain numbers";
    }
    if (isCorporate) {
      if (!corporateDetails.companyName.trim()) {
        newErrors.companyName = "Company name is required";
      }
      if (!corporateDetails.gstNo.trim()) {
        newErrors.gstNo = "GSTIN is required";
      } else if (!isValidGSTIN(corporateDetails.gstNo)) {
        newErrors.gstNo =
          "Invalid GSTIN format (must be 15 alphanumeric characters)";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassengers = () => {
    const newPassengerErrors = [];

    passengers.forEach((p, index) => {
      if (index === 0 && firstPassengerSameAsCustomer) {
        return;
      }
      const err = {};

      if (!p.name.trim()) {
        err.name = "Passenger name required";
      }

      if (!p.mobile.trim()) {
        err.mobile = "Mobile number required";
      } else if (!isValidMobile(p.mobile)) {
        err.mobile = "Enter valid 10-digit Mobile number";
      }

      if (!p.whatsapp.trim()) {
        err.whatsapp = "Whatsapp number required";
      } else if (!isValidMobile(p.whatsapp)) {
        err.whatsapp = "Enter valid 10-digit Whatsapp number";
      }

      newPassengerErrors[index] = err;
    });

    setPassengerErrors(newPassengerErrors);

    return newPassengerErrors.every((err) => Object.keys(err).length === 0);
  };

  // Validate pricing inputs for custom bookings
  const validatePricingInputs = () => {
    const newErrors = {};
    const maxDigits = 10;
    const digitRegex = /^\d+(\.\d+)?$/;

    let requiredFields = [];
    let optionalFields = [];

    if (pricingType === "fixed") {
      // For fixed pricing: only fixedRate is required
      requiredFields = ["fixedRate"];
      optionalFields = [];
    } else {
      // For mileage pricing: core km fields required, other charges optional
      requiredFields = ["ratePerKm", "extraKmRate", "minimumKmPerDay"];
      optionalFields = [
        "driverAllowancePerDay",
        "waitingCharge",
        "nightCharges",
      ];
    }

    // Validate required fields
    requiredFields.forEach((field) => {
      const value = formData[field]?.toString().trim();

      // Check if empty
      if (!value) {
        const fieldLabel = field.replace(/([A-Z])/g, " $1").trim();
        newErrors[field] = `${fieldLabel} is required`;
      } else if (!digitRegex.test(value)) {
        const fieldLabel = field.replace(/([A-Z])/g, " $1").trim();
        newErrors[field] = `${fieldLabel} must be a valid number`;
      } else if (value.replace(".", "").length > maxDigits) {
        const fieldLabel = field.replace(/([A-Z])/g, " $1").trim();
        newErrors[field] = `${fieldLabel} cannot exceed ${maxDigits} digits`;
      }
    });

    // Validate optional fields (only if they have values)
    optionalFields.forEach((field) => {
      const value = formData[field]?.toString().trim();

      if (value && value !== "0") {
        if (!digitRegex.test(value)) {
          const fieldLabel = field.replace(/([A-Z])/g, " $1").trim();
          newErrors[field] = `${fieldLabel} must be a valid number`;
        } else if (value.replace(".", "").length > maxDigits) {
          const fieldLabel = field.replace(/([A-Z])/g, " $1").trim();
          newErrors[field] = `${fieldLabel} cannot exceed ${maxDigits} digits`;
        }
      }
    });

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const scrollToFirstError = () => {
    // customer errors first
    for (const key of Object.keys(errors)) {
      const ref = fieldRefs.current[`customer.${key}`];
      if (ref) {
        ref.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.focus();
        return;
      }
    }

    // 2passenger errors
    for (let i = 0; i < passengerErrors.length; i++) {
      const errObj = passengerErrors[i];
      if (!errObj) continue;

      for (const key of Object.keys(errObj)) {
        const ref = fieldRefs.current[`passenger.${i}.${key}`];
        if (ref) {
          ref.scrollIntoView({ behavior: "smooth", block: "center" });
          ref.focus();
          return;
        }
      }
    }
  };

  // -------------------------
  // Add Lead
  // -------------------------
  const handleAddLead = async () => {
    if (!bookingData) return;

    const isCustomerValid = validateCustomerForm();
    const isPassengerValid = validatePassengers();

    // Validation for custom bookings
    if (isCustomBooking) {
      if (!customFare) {
        toast({
          title: "Please calculate custom fare first",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      // Validate pricing inputs
      if (pricingType === "fixed") {
        if (!formData.fixedRate || Number(formData.fixedRate) <= 0) {
          toast({
            title: "Invalid Pricing",
            description: "Please enter a valid fixed rate amount",
            status: "warning",
            duration: 3000,
          });
          return;
        }
      } else {
        if (!formData.ratePerKm || Number(formData.ratePerKm) <= 0) {
          toast({
            title: "Invalid Pricing",
            description: "Please enter a valid rate per km",
            status: "warning",
            duration: 3000,
          });
          return;
        }
        if (!formData.extraKmRate || Number(formData.extraKmRate) <= 0) {
          toast({
            title: "Invalid Pricing",
            description: "Please enter a valid extra km rate",
            status: "warning",
            duration: 3000,
          });
          return;
        }
        if (!formData.minimumKmPerDay || Number(formData.minimumKmPerDay) <= 0) {
          toast({
            title: "Invalid Pricing",
            description: "Please enter valid minimum kilometres per day",
            status: "warning",
            duration: 3000,
          });
          return;
        }
      }
    }

    if (!isCustomerValid || !isPassengerValid) {
      toast({
        title: "Incomplete Details",
        description: "Please fill all required customer and passenger details",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      setTimeout(scrollToFirstError, 100);
      return;
    }

    // If no customer exists, create one first
    let updatedCustomer = customer;
    if (!customer?.customerId) {
      try {
        const token = localStorage.getItem("prima_session");
        const customerPayload = {
          name: customerForm.name,
          mobile: customerForm.mobile,
          email: customerForm.email,
          whatsapp: customerForm.whatsapp || customerForm.mobile,
          type: isCorporate ? "Corporate" : "Individual",
          city: customerForm.city || "NA",
          gender: customerForm.gender || "Other",
          language: customerForm.language || "",
          isCorporate,
          ...(isCorporate && {
            companyName: corporateDetails.companyName,
            gstNo: corporateDetails.gstNo,
          }),
        };

        const customerRes = await axios.post(
          `${BASE_URL}/management/customer`,
          customerPayload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        if (customerRes.data?.success && customerRes.data?.data) {
          updatedCustomer = {
            customerId: customerRes.data.data._id,
            name: customerForm.name,
            mobile: customerForm.mobile,
            email: customerForm.email,
            whatsapp: customerForm.whatsapp,
            type: "Individual",
            city: customerForm.city || "NA",
            gender: customerForm.gender || "Other",
            language: customerForm.language || "",
          };
          setCustomer(updatedCustomer);
          toast({
            title: "Customer created",
            status: "success",
            duration: 2000,
          });
        }
      } catch (err) {
        toast({
          title: "Error creating customer",
          description: err.response?.data?.message || err.message,
          status: "error",
          duration: 3000,
        });
        return;
      }
    } else {
      // Update existing customer in database
      const token = localStorage.getItem("prima_session");

      const updatePayload = {
        name: customerForm.name,
        email: customerForm.email,
        whatsapp: customerForm.whatsapp,
        city: customerForm.city,
        gender: customerForm.gender,
        language: customerForm.language,
        type: isCorporate ? "Corporate" : "Individual",
        isCorporate,
      };

      // Only include mobile if it has changed
      if (customerForm.mobile !== customer.mobile) {
        updatePayload.mobile = customerForm.mobile;
      }

      if (isCorporate) {
        updatePayload.companyName = corporateDetails.companyName;
        updatePayload.gstNo = corporateDetails.gstNo;
      }

      try {
        // Update customer in database
        await axios.patch(
          `${BASE_URL}/management/customer/${customer.customerId}`,
          updatePayload,
          { headers: { Authorization: `Bearer ${token}` } },
        );

        toast({
          title: "Customer updated",
          status: "success",
          duration: 2000,
        });
      } catch (err) {
        console.error("Error updating customer:", err);

        // Stop lead creation if mobile number already exists (409 Conflict)
        if (err.response?.status === 409) {
          toast({
            title: "Cannot Update Customer",
            description:
              "Mobile number already registered to another customer. Please use a different number.",
            status: "error",
            duration: 3000,
          });
          return;
        }

        toast({
          title: "Warning",
          description:
            "Customer info updated locally but failed to sync to database",
          status: "warning",
          duration: 3000,
        });
      }

      updatedCustomer = {
        ...customer,
        ...updatePayload,
      };

      setCustomer(updatedCustomer);
    }

    // Save passengers to localStorage
    localStorage.setItem("lead_passengers", JSON.stringify(passengers));

    // Create passenger records in database and store only Passenger IDs
    let passengerIds = [];

    try {
      const token = localStorage.getItem("prima_session");

      // Create passenger documents for all passengers shown in the form.
      // If first passenger is marked same as customer, force customer details.
      for (let index = 0; index < passengers.length; index++) {
        const passenger = passengers[index];
        const sourcePassenger =
          index === 0 && firstPassengerSameAsCustomer
            ? {
              name: customerForm.name,
              mobile: customerForm.mobile,
              whatsapp: customerForm.whatsapp || customerForm.mobile,
              email: customerForm.email || "",
            }
            : passenger;

        if (sourcePassenger.name && sourcePassenger.mobile) {
          try {
            const passengerPayload = {
              name: sourcePassenger.name,
              mobile: sourcePassenger.mobile,
              whatsapp: sourcePassenger.whatsapp || sourcePassenger.mobile,
              email: sourcePassenger.email || "",
            };

            const passengerRes = await axios.post(
              `${BASE_URL}/management/passenger`,
              passengerPayload,
              { headers: { Authorization: `Bearer ${token}` } },
            );

            if (passengerRes.data?.success && passengerRes.data?.data?._id) {
              const passengerId = passengerRes.data.data._id;
              passengerIds.push(passengerId);
            }
          } catch (passengerError) {
            console.error("Error creating passenger:", passengerError);
            // Continue with other passengers even if one fails
          }
        }
      }

    } catch (error) {
      console.error("Error in passenger creation:", error);
    }

    try {
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("User not logged in");

      const pickupDateTime = parsePickupDateTime(
        bookingData.pickupdate,
        bookingData.pickuptime,
      );

      const returnDateTime = parseReturnDate(bookingData.returndate);

      const tripType =
        bookingData.tripType === "RT"
          ? "outstationRoundTrip"
          : bookingData.tripType === "OW"
            ? "outstationOneWay"
            : bookingData.tripType === "AT"
              ? "airportTransfer"
              : "localRental";

      const totalFare =
        Number(fareBreakup?.customerPayable) ||
        (isCustomBooking ? customFare?.pricingOutputs?.totalFare : 0);

      if (isNaN(totalFare) || totalFare <= 0) {
        console.error("TOTAL FARE IS INVALID:", {
          finalTotal,
          fareBreakup,
          customFare,
        });
        toast({
          title: "Fare calculation error",
          description: "Fare calculation failed. Check vehicle pricing.",
          status: "error",
        });
        return;
      }

      // Calculate final price after discount
      const discountAmount = Number(manualDiscount) || 0;
      const finalDiscountedPrice = Math.max(0, totalFare - discountAmount);

      // For local rental, use fromLocation as toLocation if toLocation is not provided
      const destinationLocation = bookingData.to
        ? typeof bookingData.to === "object"
          ? bookingData.to?.address || bookingData.to
          : bookingData.to
        : typeof bookingData.from === "object"
          ? bookingData.from?.address || bookingData.from
          : bookingData.from;

      // If custom booking, create custom booking record first
      let customBookingId = null;
      if (isCustomBooking) {
        try {
          const customBookingPayload = {
            pickupLocation:
              typeof bookingData.from === "object"
                ? bookingData.from?.address || bookingData.from
                : bookingData.from,
            dropLocation: destinationLocation,
            pickupDateTime,
            returnDateTime,
            vehicleSegment: bookingData.vehicleSegmentId,
            pricingType,
            bookingType: "custom",
            pricingInputs: {
              distance: Number(bookingData.distance) || 0,
              ...(pricingType === "fixed"
                ? { baseRate: Number(formData.fixedRate) }
                : {
                  perKmRate: Number(formData.ratePerKm),
                  extraKmRate: Number(formData.extraKmRate) || 0,
                  driverAllowance:
                    Number(formData.driverAllowancePerDay) || 0,
                  waitingCharge: Number(formData.waitingCharge) || 0,
                  nightCharges: Number(formData.nightCharges) || 0,
                  tollTax: Number(globalSettings.tollTaxPerKm) || 0,
                  minimumKmPerDay: Number(formData.minimumKmPerDay) || 0,
                }),
              ...(globalSettings.customCommission
                ? { globalCommission: Number(globalSettings.customCommission) || 0 }
                : {}),
            },
            pricingOutputs: {
              chargedDistance: Number(bookingData.distance) || 0,
              totalFare: finalDiscountedPrice,
            },
            discount: discountAmount,
            finalPrice: finalDiscountedPrice,
            tokenAmount: Number(tokenAmount) || 0,
            customer: updatedCustomer.customerId,
            notes: tripNotes?.trim() || "Custom booking from results",
            specificpickuplocation: specificPickupLocation,
            specificdroplocation: specificDropLocation,
            roofCarrier,
          };

          const customBookingRes = await axios.post(
            `${BASE_URL}/management/custom-booking`,
            customBookingPayload,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          if (customBookingRes.data?.data?._id) {
            customBookingId = customBookingRes.data.data._id;
            toast({
              title: "Custom Booking Created",
              description: `Custom booking ${customBookingRes.data.data.customBookingID} created successfully`,
              status: "success",
              duration: 2000,
            });
          }
        } catch (customBookingError) {
          console.error("Error creating custom booking:", customBookingError);
          toast({
            title: "Custom Booking Error",
            description:
              customBookingError.response?.data?.message ||
              "Failed to create custom booking",
            status: "error",
            duration: 3000,
          });
          // Continue with lead creation even if custom booking fails
        }
      }

      // Create lead record
      const payload = {
        fromLocation:
          typeof bookingData.from === "object"
            ? bookingData.from?.address || bookingData.from
            : bookingData.from,
        toLocation: destinationLocation,
        pickupDateTime,
        carType: bookingData.vehicleSegmentId,
        customerId: updatedCustomer.customerId,
        passengerIds: passengerIds,
        bookingType: isCustomBooking ? 'custom' : (bookingData.packageId ? 'package' : 'regular'),
        notes: isCustomBooking
          ? `Custom booking - ${tripNotes?.trim() || "No notes"}`
          : (tripNotes?.trim() || "No notes"),
        roofCarrier,
        tripDetails: {
          type: tripType,
          distance: Number(bookingData.distance) || 0,
          nightHours: Number(bookingData.nightHours) || 0,
          waitingHours: Number(bookingData.waitingHours) || 0,
          hours: Number(bookingData.hours) || 0,
          stops: (bookingData.stops || [])
            .map((stop) => {
              if (typeof stop === "object" && stop !== null) {
                return stop.address || stop.name || stop;
              }
              return stop || "";
            })
            .filter((stop) => stop && stop.trim()),
          returnDate: bookingData.returndate || null,
        },
        vehicleSegmentId: bookingData.vehicleSegmentId,
        fareDetails: {
          baseFare: totalFare,
          discounts: discountAmount,
          total: finalDiscountedPrice,
        },
        ...(isCustomBooking && customBookingId && { customBookingId }),
        specificpickuplocation: specificPickupLocation,
        specificdroplocation: specificDropLocation,
        sendWhatsappMsg: sendWhatsappMsg ? true : false,
      };

      const res = await axios.post(`${BASE_URL}/management/lead`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const savedId = res.data?.data?._id || res.data?._id;
      const savedLeadID = res.data?.data?.leadID || res.data?.leadID;
      setLeadId(savedId);
      localStorage.setItem("currentLeadId", savedId); // Store MongoDB _id
      localStorage.setItem("currentLeadID", savedLeadID); // Store human-readable leadID
      if (isCustomBooking && customBookingId) {
        localStorage.setItem("currentCustomBookingId", customBookingId);
      }

      toast({
        title: "Lead Added",
        description: `Lead ${savedLeadID} created successfully${isCustomBooking ? " with custom booking" : ""}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Error adding lead:",
        error.response?.data || error.message,
      );
      toast({
        title: "Error adding lead",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // -------------------------
  // Send Token Request
  // -------------------------
  const handleSendToken = async () => {
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("User not logged in");

      const currentLeadId = localStorage.getItem("currentLeadId"); // This is MongoDB _id
      const currentLeadID = localStorage.getItem("currentLeadID"); // This is human-readable leadID
      const currentCustomBookingId = localStorage.getItem(
        "currentCustomBookingId",
      );
      if (!currentLeadId) throw new Error("Lead ID missing! Add lead first.");

      const totalFare =
        isCustomBooking && customFare
          ? Number(customFare.pricingOutputs?.totalFare || 0)
          : Number(finalTotal) || 0;

      if (isNaN(totalFare) || totalFare <= 0) {
        console.error("TOTAL FARE IS INVALID:", {
          finalTotal,
          customFare,
          isCustomBooking,
        });
        toast({
          title: "Fare calculation error",
          description: "Fare calculation failed. Check vehicle pricing.",
          status: "error",
        });
        return;
      }

      // Use token amount as payment amount
      const tokenAmt = Number(tokenAmount) || 0;
      if (isNaN(tokenAmt) || tokenAmt < 1) {
        toast({
          title: "Invalid token",
          description: "Token amount must be at least ₹1.",
          status: "warning",
          duration: 3000,
        });
        return;
      }

      const payload = {
        amount: tokenAmt,
        userPhone: customer?.mobile || customerForm.mobile || "",
        userEmail: customer?.email || customerForm.email || "",
        orderId: currentLeadID || `ORD${Date.now()}`,
        leadId: currentLeadId,
        customerId: customer?.customerId || null,
        ...(isCustomBooking &&
          currentCustomBookingId && {
          customBookingId: currentCustomBookingId,
        }),
      };

      // Validate required fields
      payload.userPhone = normalizeIndianMobile(payload.userPhone);

      if (!isValidMobile(payload.userPhone)) {
        toast({
          title: "Invalid Phone",
          description: "Please enter a valid 10-digit phone number.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      if (payload.userEmail && !isValidEmail(payload.userEmail)) {
        toast({
          title: "Invalid Email",
          description: "Please enter a valid email address.",
          status: "error",
          duration: 3000,
        });
        return;
      }

      const res = await axios.post(`${BASE_URL}/payments/send-token`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const paymentId =
        res.data?.paymentLinkId ||
        res.data?.data?._id ||
        res.data?.data?.paymentId ||
        res.data?._id;
      const paymentLink = res.data?.paymentLink || res.data?.data?.paymentLink;

      toast({
        title: "Token Request Sent!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Clear localStorage after successful token request
      localStorage.removeItem("lead_booking_data");
      localStorage.removeItem("customerDetails");
      localStorage.removeItem("selectedVehicleId");
      localStorage.removeItem("lead_passengers");
      localStorage.removeItem("selectedLeadId");
      localStorage.removeItem("selectedLeadData");
      localStorage.removeItem("currentCustomBookingId");

      navigate("/bookings/lead/booking-status", {
        state: {
          leadId: currentLeadID, // Pass human-readable leadID for display
          leadMongoId: currentLeadId, // Pass MongoDB _id for API calls
          customBookingId: currentCustomBookingId,
          amount: totalFare,
          systemCalculated: finalTotal,
          negotiatedTotal: finalPrice,
          tokenAmount: tokenAmount,
          bookingData: bookingData,
          customerData: customer,
          passengers: passengers,
          paymentId,
          paymentLink,
          isCustomBooking,
        },
      });

      // console.log("Send-token Response:", res.data);
    } catch (error) {
      console.error("Token Error: ", error?.response?.data || error.message);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message ||
        "Unknown error occurred";
      toast({
        title: "Error Sending Token",
        description: `Request failed: ${errorMessage}`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Box bg="#F3F6FD" minH="100vh" p={{ base: 4, md: 8 }}>
        <Shimmer rows={6} rowHeight={24} />
      </Box>
    );
  }

  if (error || !bookingData) {
    return (
      <Box bg="#F3F6FD" minH="100vh" p={{ base: 4, md: 8 }}>
        <Text color="red.500">
          {error ||
            "Missing booking data. Please go back and select a vehicle."}
        </Text>
      </Box>
    );
  }

  const normalizedTripType = String(bookingData.tripType || "").toUpperCase();
  const isLocalRental = ["LR", "HR", "LOCAL", "LOCALRENTAL", "LOCAL_RENTAL"].includes(
    normalizedTripType,
  );
  const destinationValue =
    (bookingData.to?.address || bookingData.to || "").toString().trim().toLowerCase();
  const shouldShowDropLocation = !isLocalRental && destinationValue !== "local";

  return (
    <Box bg="#F3F6FD" minH="100vh" p={{ base: 4, md: 8 }}>
      <Box mb={4}>
        <BreadCrumbMaker
          items={[
            { link: "/bookings", title: "Bookings" },
            { link: "/bookings/lead/search", title: "Add Booking", on: true },
            { link: "/bookings/lead/results", title: "Add Leads", on: true },
            { link: "", title: "Review Page", on: true },
          ]}
          on={true}
        />
      </Box>

      {/* Two Column Layout */}
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6}>
        {/* LEFT COLUMN - Form */}
        <VStack align="stretch" spacing={4}>
          {/* Vehicle Card - Top Left */}
          <Box
            bg="white"
            p={6}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <VStack align="stretch" spacing={3}>
              {bookingData.tripType === "RT" && (
                <Badge colorScheme="blue" w="fit-content" fontSize="sm">
                  (Roundtrip)
                </Badge>
              )}
              {bookingData.tripType === "OW" && (
                <Badge colorScheme="green" w="fit-content" fontSize="sm">
                  (Oneway)
                </Badge>
              )}
              {bookingData.tripType === "AT" && (
                <Badge colorScheme="purple" w="fit-content" fontSize="sm">
                  (Airport Transfer)
                </Badge>
              )}

              <RouteFlow
                from={bookingData.from}
                to={bookingData.to}
                stops={bookingData.stops || []}
                tripType={bookingData.tripType}
              />

              <Grid templateColumns="repeat(2, 1fr)" gap={3} mt={2}>
                <Text fontSize="md" color="gray.700">
                  <strong>Pickup Location:</strong>{" "}
                  {bookingData.from?.address || bookingData.from || "No pickup location selected"}
                </Text>

                {shouldShowDropLocation && <Text fontSize="md" color="gray.700">
                  <strong>Drop Location:</strong>{" "}
                  {bookingData.to?.address || bookingData.to || "No drop location selected"}
                </Text>
                }


                <Text fontSize="md" color="gray.700">
                  <strong>Pickup Date and Time:</strong> {bookingData.pickupdate},{" "}
                  {formatTime12(bookingData.pickuptime)}
                </Text>

                {bookingData.tripType === "RT" && bookingData.returndate && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Return Date:</strong> {bookingData.returndate}
                  </Text>
                )}

                {bookingData.tripType === "OW" && bookingData.returndate && (
                  <Text fontSize="md" color="gray.700">
                    <strong>End Date:</strong> {bookingData.returndate}
                  </Text>
                )}

                {(() => {
                  const resolvedTripDays = Number(
                    fareBreakup?.tripDays ??
                    fareBreakup?.numberOfDays ??
                    bookingData?.numOfDays ??
                    0,
                  );
                  const shouldShowTripDays =
                    !!bookingData?.returndate ||
                    bookingData?.tripType === "RT" ||
                    (bookingData?.tripType === "OW" && bookingData?.stops?.length > 0);

                  if (shouldShowTripDays && resolvedTripDays > 0) {
                    return (
                      <Text fontSize="md" color="gray.700">
                        <strong>Trip Duration:</strong> {resolvedTripDays} days
                      </Text>
                    );
                  }

                  if (fareBreakup?.durationText) {
                    return (
                      <Text fontSize="md" color="gray.700">
                        <strong>Trip Duration:</strong> {fareBreakup.durationText}
                      </Text>
                    );
                  }

                  return null;
                })()}


                {bookingData.tripType === "LR" && bookingData.hours && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Rental Duration:</strong> {bookingData.hours} hours
                  </Text>
                )}

                {bookingData.distance > 0 && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Actual Distance:</strong> {bookingData.distance} kms
                  </Text>
                )}

                {(fareBreakup.includedKm > 0) && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Charged Distance:</strong> {fareBreakup.includedKm} kms
                  </Text>
                )}

                {fareBreakup?.tollTax > 0 && !fareBreakup?.isTollIncludedInPrice && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Estimated Toll Tax:</strong> ₹{fareBreakup?.tollTax.toFixed(2)}
                  </Text>
                )}
              </Grid>

              <Divider display="block" />

              <Grid templateColumns="repeat(2, 1fr)" gap={3}>

                <Text fontSize="md" color="gray.700">
                  <strong>Car Type:</strong>{" "}
                  {bookingData?.vehicleSegmentName ||
                    vehicle?.segmentName ||
                    bookingData?.carDetails?.name ||
                    "Car"}{" "}
                  - {vehicle?.vehicleTypes || bookingData?.carDetails?.type || "Type not specified"}
                </Text>

                {vehicle?.passengerCapacity && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Seats:</strong> {vehicle.passengerCapacity || bookingData?.carDetails?.seat || "N/A"} + 1 Driver
                  </Text>
                )}

                {vehicle?.luggageCapacity && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Luggage Capacity:</strong> {vehicle.luggageCapacity} bags
                  </Text>
                )}

                {vehicle?.amenities && Array.isArray(vehicle.amenities) && vehicle.amenities.length > 0 && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Amenities:</strong> {vehicle.amenities.join(", ")}
                  </Text>
                )}

                {vehicle?.fuelTypes && Array.isArray(vehicle.fuelTypes) && vehicle.fuelTypes.length > 0 && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Fuel Types:</strong> {vehicle.fuelTypes.join(", ")}
                  </Text>
                )}

                {resolvedTripPricing?.premium?.isPremium && (
                  <Text fontSize="md" color="gray.700">
                    <strong>Premium Addons:</strong>{" "}
                    {Array.isArray(resolvedTripPricing?.premium?.addons) &&
                      resolvedTripPricing.premium.addons
                        .map((addon) => String(addon || "").trim())
                        .filter(Boolean).length > 0
                      ? resolvedTripPricing.premium.addons
                        .map((addon) => String(addon || "").trim())
                        .filter(Boolean)
                        .join(", ")
                      : "Yes"}
                  </Text>
                )}
              </Grid>

            </VStack>
          </Box>

          {(() => {
            const { includes, excludes } = getPricingInclusions();
            const isLocalRental = bookingData?.tripType === "LR";
            const fareIncludes = Array.isArray(fareBreakup?.includes)
              ? fareBreakup.includes
              : [];
            const fareExcludes = Array.isArray(fareBreakup?.excludes)
              ? fareBreakup.excludes
              : [];
            const resolvedIncludes =
              isLocalRental && fareIncludes.length > 0 ? fareIncludes : includes;
            const resolvedExcludes =
              isLocalRental && fareExcludes.length > 0 ? fareExcludes : excludes;
            const includedKm = Number(fareBreakup?.includedKm || 0);
            const includedHours = Number(
              fareBreakup?.includedHours || bookingData?.hours || 0,
            );
            const extraKmRate = Number(fareBreakup?.extraKmRate || 0);
            const extraHourRate = Number(fareBreakup?.extraHourRate || 0);
            const packageInclusions = normalizeTags(
              bookingData?.inclusionsAndExclusions,
            );

            const formatLabel = (label) => {
              const raw = String(label || "").trim();
              const key = raw.toLowerCase();

              const labelMap = {
                "toll": "Tolls",
                "tolls": "Tolls",
                "state tax": "State taxes",
                "state taxes": "State taxes",
                "parking": "Parking Fees",
                "entry fees": "Entry Fees",
                "parking fees": "Parking Fees",
                "parking & entry": "Parking & Entry Fees",
                "parking & entry fees": "Parking & Entry Fees",
                "night charge": "Night Charge",
                "night charges": "Night Charge",
                "vip entry": "Permission for VIP entry",
                "permission for vip entry": "Permission for VIP entry",
                "driver": "Driver allowance",
                "driver allowance": "Driver allowance",
                "fuel": "Fuel charges",
                "fuel charges": "Fuel charges",
                "taxes and fees": "Taxes and Fees",
              };

              return labelMap[key] || raw;
            };

            const uniqueByValue = (items) => {
              const seen = new Set();
              return items.filter((item) => {
                const key = String(item || "").trim().toLowerCase();
                if (!key || seen.has(key)) return false;
                seen.add(key);
                return true;
              });
            };

            const includedItems = [];
            if (isLocalRental) {
              if (includedHours > 0 || includedKm > 0) {
                const hoursText = String(Math.max(0, Math.trunc(includedHours))).padStart(2, "0");
                const kmsText = Math.max(0, Math.trunc(includedKm));
                includedItems.push(`Included ${hoursText}hrs & ${kmsText}Km`);
              }
            } else {
              if (includedKm > 0) {
                includedItems.push(`Included ${includedKm}km`);
              }
              includedItems.push("Waiting time: 30 mins for Pickup/Drop");
            }
            if (bookingData?.tripType === "AT") {
              includedItems.push("Only one pickup and drop");
            }
            includedItems.push(...packageInclusions.map(formatLabel));
            includedItems.push(...resolvedIncludes.map(formatLabel));

            const excludedItems = [];
            const localRentalBeyondPackage = [];

            if (isLocalRental) {
              if (extraKmRate > 0) {
                localRentalBeyondPackage.push(
                  `₹${extraKmRate.toFixed(2)}/km beyond ${Math.max(0, Math.trunc(includedKm))} kms`,
                );
              }
              if (extraHourRate > 0) {
                localRentalBeyondPackage.push(
                  `₹${extraHourRate.toFixed(2)}/hr for extra time`,
                );
              }
            } else {
              if (extraKmRate > 0) {
                excludedItems.push(
                  `Extra Km: ₹${extraKmRate.toFixed(2)}/km beyond the included kms`,
                );
              }
              if (waitingCharge > 0) {
                excludedItems.push(
                  `Post 30 mins: ₹${Number(waitingCharge).toFixed(2)}/30 mins`,
                );
              }
            }
            excludedItems.push(...resolvedExcludes.map(formatLabel));

            const finalIncluded = uniqueByValue(includedItems);
            const finalExcluded = uniqueByValue(excludedItems);
            const showExclusionsColumn =
              !bookingData?.packageId &&
              (finalExcluded.length > 0 || (isLocalRental && localRentalBeyondPackage.length > 0));

            if (finalIncluded.length === 0 && !showExclusionsColumn) return null;

            return (
              <Box
                bg="white"
                p={6}
                rounded="md"
                border="1px solid #E8EDF7"
                boxShadow="sm"
              >
                <Grid
                  templateColumns={showExclusionsColumn ? { base: "1fr", md: "1fr 1fr" } : "1fr"}
                  gap={8}
                >
                  <Box>
                    <Text fontSize="xl" fontWeight="600" mb={4} color="gray.800">
                      {isLocalRental ? "Inclusions" : "Included"}
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {finalIncluded.map((item, index) => (
                        <Text key={`inc-${index}`} fontSize="md" color="gray.700">
                          • {item}
                        </Text>
                      ))}
                    </VStack>
                  </Box>

                  {showExclusionsColumn && (
                    <Box>
                      <Text fontSize="xl" fontWeight="600" mb={4} color="gray.800">
                        Exclusions (Not included)
                      </Text>
                      {isLocalRental && localRentalBeyondPackage.length > 0 && (
                        <Box mb={3}>
                          <Text fontSize="md" color="gray.700" mb={1}>
                            Charges beyond the package:
                          </Text>
                          <VStack align="stretch" spacing={1} pl={1}>
                            {localRentalBeyondPackage.map((item, index) => (
                              <Text key={`lr-extra-${index}`} fontSize="md" color="gray.700">
                                - {item}
                              </Text>
                            ))}
                          </VStack>
                        </Box>
                      )}
                      <VStack align="stretch" spacing={2}>
                        {finalExcluded.map((item, index) => (
                          <Text key={`exc-${index}`} fontSize="md" color="gray.700">
                            • {item}
                          </Text>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Grid>
              </Box>
            );
          })()}

          {/* PRICING TYPE AND DETAILS - Only for Custom Bookings */}
          {isCustomBooking && (
            <Box
              bg="white"
              p={6}
              rounded="md"
              border="1px solid #E8EDF7"
              boxShadow="sm"
            >
              {/* PRICING TYPE */}
              <Box mb={6}>
                <Heading fontSize="lg" mb={4} color="blue.600">
                  Pricing Type <span style={{ color: "red" }}>*</span>
                </Heading>
                <RadioGroup value={pricingType} onChange={setPricingType} isDisabled={isCustomBooking && leadId}>
                  <Stack direction="row" spacing={8}>
                    <Radio value="fixed" colorScheme="blue">
                      <Text fontWeight="600">Fixed Rate</Text>
                    </Radio>
                    <Radio value="mileage" colorScheme="blue">
                      <Text fontWeight="600">Mileage Based</Text>
                    </Radio>
                  </Stack>
                </RadioGroup>
              </Box>



              <Divider my={6} />

              <Box mb={6}>
                <Heading fontSize="md" mb={4} color="blue.600">
                  Inclusions/Exclusions
                </Heading>

                <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
                  <Checkbox
                    isChecked={customInclusionChecks.tolls}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      // 1. update inclusion state
                      setCustomInclusionChecks((prev) => ({
                        ...prev,
                        tolls: checked,
                      }));
                    }}
                    isDisabled={isCustomBooking && leadId}
                  >
                    Tolls
                  </Checkbox>

                  <Checkbox
                    isChecked={customInclusionChecks.stateTaxes}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setCustomInclusionChecks((prev) => ({
                        ...prev,
                        stateTaxes: checked,
                      }));
                    }}
                    isDisabled={isCustomBooking && leadId}
                  >
                    State taxes
                  </Checkbox>
                  <Checkbox
                    isChecked={customInclusionChecks.parkingFees}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setCustomInclusionChecks((prev) => ({
                        ...prev,
                        parkingFees: checked,
                      }));
                    }}
                    isDisabled={isCustomBooking && leadId}
                  >
                    Parking Fees
                  </Checkbox>
                  <Checkbox
                    isChecked={customInclusionChecks.entryFees}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setCustomInclusionChecks((prev) => ({
                        ...prev,
                        entryFees: checked,
                      }));
                    }}
                    isDisabled={isCustomBooking && leadId}
                  >
                    Entry Fees
                  </Checkbox>
                </Grid>
              </Box>

              <Divider my={6} />

              {/* PRICING DETAILS */}
              <Box>
                <Heading fontSize="md" mb={4} color="blue.600">
                  Pricing Details
                </Heading>
                {pricingType === "fixed" ? (
                  <VStack align="stretch" spacing={4}>
                    <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                      <GridItem>
                        <FormControl isInvalid={!!formErrors.fixedRate}>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Final Amount <span style={{ color: "red" }}>*</span>
                          </FormLabel>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="number"
                              name="fixedRate"
                              value={formData.fixedRate}
                              onChange={handleChange}
                              placeholder="Final booking amount"
                              h="40px"
                              isReadOnly={isCustomBooking && leadId}
                              opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                              bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                              cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                              borderColor={
                                formErrors.fixedRate ? "red.500" : undefined
                              }
                              paddingLeft="30px"
                            />
                          </InputGroup>
                          {formErrors.fixedRate && (
                            <FormErrorMessage fontSize="sm" mt={1}>
                              {formErrors.fixedRate}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </GridItem>
                    </Grid>
                    <Button
                      colorScheme="blue"
                      onClick={
                        handleCalculateFareClick
                      }
                      isLoading={calculatingFare}
                      w="fit-content"
                      isDisabled={isCustomBooking && leadId}
                      title={isCustomBooking && leadId ? "Cannot recalculate after lead creation" : ""}
                    >
                      Calculate Fare
                    </Button>
                  </VStack>
                ) : (
                  <VStack align="stretch" spacing={4}>
                    <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                      <GridItem>
                        <FormControl isInvalid={!!formErrors.ratePerKm}>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Rate Per KM <span style={{ color: "red" }}>*</span>
                          </FormLabel>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="number"
                              name="ratePerKm"
                              value={formData.ratePerKm}
                              onChange={handleChange}
                              placeholder="/km"
                              h="40px"
                              isReadOnly={isCustomBooking && leadId}
                              opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                              bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                              cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                              borderColor={
                                formErrors.ratePerKm ? "red.500" : undefined
                              }
                              paddingLeft="30px"
                            />
                          </InputGroup>
                          {formErrors.ratePerKm && (
                            <FormErrorMessage fontSize="sm" mt={1}>
                              {formErrors.ratePerKm}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isInvalid={!!formErrors.extraKmRate}>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Extra Kilometre Rate <span style={{ color: "red" }}>*</span>
                          </FormLabel>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="number"
                              name="extraKmRate"
                              value={formData.extraKmRate}
                              onChange={handleChange}
                              placeholder="/km"
                              h="40px"
                              isReadOnly={isCustomBooking && leadId}
                              opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                              bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                              cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                              borderColor={
                                formErrors.extraKmRate ? "red.500" : undefined
                              }
                              paddingLeft="30px"
                            />
                          </InputGroup>
                          {formErrors.extraKmRate && (
                            <FormErrorMessage fontSize="sm" mt={1}>
                              {formErrors.extraKmRate}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Driver Allowance/Day
                          </FormLabel>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="number"
                              name="driverAllowancePerDay"
                              value={formData.driverAllowancePerDay}
                              onChange={handleChange}
                              placeholder="Amount per day"
                              h="40px"
                              isReadOnly={isCustomBooking && leadId}
                              opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                              bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                              cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                              paddingLeft="30px"
                            />
                          </InputGroup>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Waiting Charge / 30 Minutes
                          </FormLabel>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="number"
                              name="waitingCharge"
                              value={formData.waitingCharge}
                              onChange={handleChange}
                              placeholder="Waiting charge"
                              h="40px"
                              isReadOnly={isCustomBooking && leadId}
                              opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                              bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                              cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                              paddingLeft="30px"
                            />
                          </InputGroup>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Night Charge / Night
                          </FormLabel>
                          <InputGroup>
                            <InputLeftElement
                              pointerEvents="none"
                              color="gray.500"
                            >
                              ₹
                            </InputLeftElement>
                            <Input
                              type="number"
                              name="nightCharges"
                              value={formData.nightCharges}
                              onChange={handleChange}
                              placeholder="Night charge"
                              h="40px"
                              isReadOnly={isCustomBooking && leadId}
                              opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                              bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                              cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                              paddingLeft="30px"
                            />
                          </InputGroup>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl isInvalid={!!formErrors.minimumKmPerDay}>
                          <FormLabel fontSize="sm" fontWeight="500">
                            Minimum Kilometres / Day <span style={{ color: "red" }}>*</span>
                          </FormLabel>
                          <Input
                            type="number"
                            name="minimumKmPerDay"
                            value={formData.minimumKmPerDay}
                            onChange={handleChange}
                            placeholder="Minimum kms/day"
                            h="40px"
                            isReadOnly={isCustomBooking && leadId}
                            opacity={(isCustomBooking && leadId) ? 0.7 : 1}
                            bg={(isCustomBooking && leadId) ? "gray.100" : "white"}
                            cursor={(isCustomBooking && leadId) ? "not-allowed" : "auto"}
                            borderColor={
                              formErrors.minimumKmPerDay ? "red.500" : undefined
                            }
                          />
                          {formErrors.minimumKmPerDay && (
                            <FormErrorMessage fontSize="sm" mt={1}>
                              {formErrors.minimumKmPerDay}
                            </FormErrorMessage>
                          )}
                        </FormControl>
                      </GridItem>
                    </Grid>
                    <Button
                      colorScheme="blue"
                      onClick={handleCalculateFareClick}
                      isLoading={calculatingFare}
                      w="fit-content"
                      isDisabled={isCustomBooking && leadId}
                      title={isCustomBooking && leadId ? "Cannot recalculate after lead creation" : ""}
                    >
                      Calculate Fare
                    </Button>
                  </VStack>
                )}
              </Box>


            </Box>
          )}


          {/* Trip Notes with Pickup/Drop */}
          <Box
            bg="white"
            p={6}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <Box>
                <FormLabel mb={2}>Trip Notes</FormLabel>
                {/* Pickup/Drop display */}
                <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4} mb={4}>
                  <Box>
                    <strong>Pickup Location:</strong>
                    <input
                      type="text"
                      placeholder="Enter specific pickup location"
                      value={specificPickupLocation}
                      onChange={(e) => setSpecificPickupLocation(e.target.value)}
                      style={{
                        width: "300px",
                        maxWidth: "100%",
                        padding: "8px",
                        border: "1px solid #E8EDF7",
                        borderRadius: "4px",
                        backgroundColor: leadId ? "#F7FAFC" : "white",
                        opacity: leadId ? 0.7 : 1,
                        cursor: leadId ? "not-allowed" : "auto",
                      }}
                      readOnly={leadId}
                    />
                  </Box>
                  <Box>
                    <strong>Drop Location:</strong>
                    <input
                      type="text"
                      placeholder="Enter specific drop location"
                      value={specificDropLocation}
                      onChange={(e) => setSpecificDropLocation(e.target.value)}
                      style={{
                        width: "300px",
                        maxWidth: "100%",
                        padding: "8px",
                        border: "1px solid #E8EDF7",
                        borderRadius: "4px",
                        backgroundColor: leadId ? "#F7FAFC" : "white",
                        opacity: leadId ? 0.7 : 1,
                        cursor: leadId ? "not-allowed" : "auto",
                      }}
                      readOnly={leadId}
                    />
                  </Box>
                </Grid>
                <Input
                  ref={(el) => (fieldRefs.current["trip.notes"] = el)}
                  value={tripNotes}
                  onChange={(e) => setTripNotes(e.target.value)}
                  placeholder="Enter notes"
                  isReadOnly={leadId}
                  opacity={leadId ? 0.7 : 1}
                  bg={leadId ? "gray.100" : "white"}
                  cursor={leadId ? "not-allowed" : "auto"}
                />
              </Box>

              <Box display="flex" alignItems="flex-end">
                <Checkbox
                  isChecked={roofCarrier}
                  onChange={(e) => setRoofCarrier(e.target.checked)}
                  isDisabled={leadId}
                >
                  Roof Carrier
                </Checkbox>
              </Box>
            </Grid>
          </Box>

          {/* Search Lead Section */}
          <Box
            bg="white"
            p={6}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <Text fontWeight="600" mb={3}>
              Search Existing Customer
            </Text>
            <Box position="relative">
              <Input
                placeholder="Enter name or mobile number..."
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={() =>
                  setTimeout(() => setShowSearchDropdown(false), 200)
                }
                onFocus={() =>
                  searchResults.length > 0 && setShowSearchDropdown(true)
                }
                isReadOnly={leadId}
                opacity={leadId ? 0.7 : 1}
                bg={leadId ? "gray.100" : "white"}
                cursor={leadId ? "not-allowed" : "auto"}
              />
              {isSearching && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Searching...
                </Text>
              )}
              {showSearchDropdown && searchResults.length > 0 && (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  bg="white"
                  border="1px solid #E8EDF7"
                  borderRadius="md"
                  boxShadow="lg"
                  zIndex={10}
                  mt={1}
                  maxH="200px"
                  overflowY="auto"
                >
                  {searchResults.map((lead, idx) => (
                    <Box
                      key={idx}
                      p={3}
                      cursor="pointer"
                      _hover={{ bg: "gray.50" }}
                      borderBottom="1px solid #E8EDF7"
                      onClick={() => handleLeadSelect(lead)}
                    >
                      <Text fontWeight="600">{lead.name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {lead.mobile}
                      </Text>
                      {lead.email && (
                        <Text fontSize="xs" color="gray.500">
                          {lead.email}
                        </Text>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Customer Details */}
          <Box
            bg="white"
            p={6}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <Text fontSize="18px" fontWeight="600" mb={4}>
              Customer Details
            </Text>

            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              <FormControl isInvalid={errors.name}>
                <FormLabel>
                  Name <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Input
                  ref={(el) => (fieldRefs.current["customer.name"] = el)}
                  value={customerForm.name}
                  onChange={(e) =>
                    handleCustomerFormChange("name", e.target.value)
                  }
                  placeholder="Enter Name"
                  isReadOnly={leadId}
                  opacity={leadId ? 0.7 : 1}
                  bg={leadId ? "gray.100" : "white"}
                  cursor={leadId ? "not-allowed" : "auto"}
                />
                {errors.name && (
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={errors.email}>
                <FormLabel>
                  Email ID
                </FormLabel>
                <Input
                  ref={(el) => (fieldRefs.current["customer.email"] = el)}
                  value={customerForm.email}
                  onChange={(e) =>
                    handleCustomerFormChange("email", e.target.value)
                  }
                  placeholder="Enter Email"
                  isReadOnly={leadId}
                  opacity={leadId ? 0.7 : 1}
                  bg={leadId ? "gray.100" : "white"}
                  cursor={leadId ? "not-allowed" : "auto"}
                />
                {errors.email && (
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={errors.mobile}>
                <FormLabel>
                  Mobile Number <span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Input
                  ref={(el) => (fieldRefs.current["customer.mobile"] = el)}
                  value={customerForm.mobile}
                  onChange={(e) =>
                    handleCustomerFormChange("mobile", e.target.value)
                  }
                  placeholder="Enter Mobile Number"
                  maxLength={10}
                  isReadOnly={leadId}
                  opacity={leadId ? 0.7 : 1}
                  bg={leadId ? "gray.100" : "white"}
                  cursor={leadId ? "not-allowed" : "auto"}
                />
                {errors.mobile && (
                  <FormErrorMessage>{errors.mobile}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={errors.whatsapp}>
                <FormLabel>
                  Whatsapp Number<span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Input
                  ref={(el) => (fieldRefs.current["customer.whatsapp"] = el)}
                  value={customerForm.whatsapp}
                  onChange={(e) =>
                    handleCustomerFormChange("whatsapp", e.target.value)
                  }
                  placeholder="Enter Whatsapp Number"
                  isDisabled={whatsappSameAsMobile || leadId}
                  maxLength={10}
                />
                {errors.whatsapp && (
                  <FormErrorMessage>{errors.whatsapp}</FormErrorMessage>
                )}
                <Checkbox
                  mt={2}
                  size="sm"
                  isChecked={whatsappSameAsMobile}
                  onChange={(e) => {
                    setWhatsappSameAsMobile(e.target.checked);
                    if (e.target.checked) {
                      setCustomerForm((prev) => ({
                        ...prev,
                        whatsapp: prev.mobile,
                      }));
                      setErrors((prev) => ({ ...prev, whatsapp: undefined }));
                    }
                  }}
                  isDisabled={leadId}
                >
                  Same as mobile number
                </Checkbox>
              </FormControl>

              <Box ref={(el) => (fieldRefs.current["customer.city"] = el)} opacity={leadId ? 0.6 : 1} pointerEvents={leadId ? "none" : "auto"}>
                <FormLabel>
                  City<span style={{ color: "red" }}>*</span>
                </FormLabel>
                <InputWithSuggestionSimple
                  name="city"
                  placeholder="Enter City"
                  value={customerForm.city}
                  filter="(cities)"
                  onChange={(value) => handleCustomerFormChange("city", value)}
                  isDisabled={leadId}
                />
                {errors.city && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.city}
                  </Text>
                )}
              </Box>

              <FormControl isInvalid={errors.gender}>
                <FormLabel>
                  Gender<span style={{ color: "red" }}>*</span>
                </FormLabel>
                <Select
                  ref={(el) => (fieldRefs.current["customer.gender"] = el)}
                  value={customerForm.gender}
                  onChange={(e) =>
                    handleCustomerFormChange("gender", e.target.value)
                  }
                  placeholder="Select Gender"
                  isDisabled={leadId}
                >
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </Select>
                {errors.gender && (
                  <FormErrorMessage>{errors.gender}</FormErrorMessage>
                )}
              </FormControl>

              <FormControl isInvalid={errors.language}>
                <FormLabel>Language</FormLabel>
                <Input
                  ref={(el) => (fieldRefs.current["customer.language"] = el)}
                  value={customerForm.language}
                  onChange={(e) => {
                    const value = e.target.value;
                    const cleaned = value.replace(/\d/g, "");
                    handleCustomerFormChange("language", cleaned);
                  }}
                  placeholder="English, Hindi"
                  isReadOnly={leadId}
                  opacity={leadId ? 0.7 : 1}
                  bg={leadId ? "gray.100" : "white"}
                  cursor={leadId ? "not-allowed" : "auto"}
                />
                {errors.language && (
                  <FormErrorMessage>{errors.language}</FormErrorMessage>
                )}
              </FormControl>
            </Grid>

            <Checkbox
              mt={4}
              isChecked={isCorporate}
              onChange={(e) => setIsCorporate(e.target.checked)}
              isDisabled={leadId}
            >
              Corporate Client
            </Checkbox>
            {isCorporate && (
              <Grid mt={4} templateColumns="repeat(2, 1fr)" gap={4}>
                <FormControl isInvalid={errors.companyName}>
                  <FormLabel>
                    Company Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Input
                    ref={(el) =>
                      (fieldRefs.current["customer.companyName"] = el)
                    }
                    value={corporateDetails.companyName}
                    onChange={(e) => {
                      setCorporateDetails((prev) => ({
                        ...prev,
                        companyName: e.target.value,
                      }));
                      // Clear error when user types
                      if (errors.companyName) {
                        setErrors((prev) => {
                          const copy = { ...prev };
                          delete copy.companyName;
                          return copy;
                        });
                      }
                    }}
                    placeholder="Enter Company Name"
                    isReadOnly={leadId}
                    opacity={leadId ? 0.7 : 1}
                    bg={leadId ? "gray.100" : "white"}
                    cursor={leadId ? "not-allowed" : "auto"}
                  />
                  {errors.companyName && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.companyName}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={errors.gstNo}>
                  <FormLabel>
                    GSTIN <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <Input
                    ref={(el) => (fieldRefs.current["customer.gstNo"] = el)}
                    value={corporateDetails.gstNo}
                    onChange={(e) => {
                      setCorporateDetails((prev) => ({
                        ...prev,
                        gstNo: e.target.value.toUpperCase(),
                      }));
                      if (errors.gstNo) {
                        setErrors((prev) => {
                          const copy = { ...prev };
                          delete copy.gstNo;
                          return copy;
                        });
                      }
                    }}
                    placeholder="Enter GSTIN"
                    maxLength={15}
                    isReadOnly={leadId}
                    opacity={leadId ? 0.7 : 1}
                    bg={leadId ? "gray.100" : "white"}
                    cursor={leadId ? "not-allowed" : "auto"}
                  />
                  {errors.gstNo && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.gstNo}
                    </Text>
                  )}
                </FormControl>
              </Grid>
            )}
          </Box>

          {/* Passenger Details */}
          <Box
            bg="white"
            p={6}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <Flex justify="space-between" align="center" mb={4}>
              <Text fontSize="18px" fontWeight="600">
                Passenger Details
                {vehicle?.passengerCapacity && (
                  <Text as="span" fontSize="sm" color="gray.500" ml={2}>
                    (Max {vehicle.passengerCapacity})
                  </Text>
                )}
              </Text>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                size="sm"
                onClick={addPassenger}
                isDisabled={leadId || (vehicle?.passengerCapacity && passengers.length >= vehicle.passengerCapacity)}
              >
                Add Passenger
              </Button>
            </Flex>

            <Checkbox
              mb={4}
              isChecked={firstPassengerSameAsCustomer}
              onChange={(e) => {
                const checked = e.target.checked;
                setFirstPassengerSameAsCustomer(checked);
                if (checked) {
                  setPassengers((prev) => {
                    const updated = [...prev];
                    updated[0] = {
                      name: customerForm.name,
                      mobile: customerForm.mobile,
                      whatsapp: customerForm.whatsapp || customerForm.mobile,
                    };
                    return updated;
                  });
                  setPassengerErrors((prev) => {
                    const updated = [...prev];
                    updated[0] = {};
                    return updated;
                  });
                }
              }}
              isDisabled={leadId}
            >
              Same as booked customer
            </Checkbox>

            {passengers
              .map((passenger, index) => ({ passenger, index }))
              .reverse()
              .map(({ passenger, index }) => (
                <Box
                  key={index}
                  mb={4}
                  p={4}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="gray.50"
                  position="relative"
                >
                  <HStack justify="space-between" mb={3}>
                    <Text fontWeight="600" color="gray.700">
                      Passenger {index + 1}
                    </Text>
                    {passengers.length > 1 && (
                      <IconButton
                        icon={<FiTrash2 />}
                        colorScheme="red"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePassenger(index)}
                        aria-label="Remove passenger"
                      />
                    )}
                  </HStack>

                  <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                    <FormControl isInvalid={passengerErrors[index]?.name}>
                      <FormLabel>
                        Name <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Input
                        ref={(el) =>
                          (fieldRefs.current["passenger." + index + ".name"] = el)
                        }
                        value={passenger.name}
                        onChange={(e) =>
                          handlePassengerChange(index, "name", e.target.value)
                        }
                        placeholder="Passenger Name"
                        bg="white"
                        isReadOnly={leadId || (index === 0 && firstPassengerSameAsCustomer)}
                        opacity={(leadId || (index === 0 && firstPassengerSameAsCustomer)) ? 0.7 : 1}
                        bgColor={(leadId || (index === 0 && firstPassengerSameAsCustomer)) ? "gray.100" : "white"}
                        cursor={(leadId || (index === 0 && firstPassengerSameAsCustomer)) ? "not-allowed" : "auto"}
                      />
                      {passengerErrors[index]?.name && (
                        <FormErrorMessage>
                          {passengerErrors[index].name}
                        </FormErrorMessage>
                      )}
                    </FormControl>

                    <FormControl isInvalid={passengerErrors[index]?.mobile}>
                      <FormLabel>
                        Mobile Number <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Input
                        ref={(el) =>
                          (fieldRefs.current[`passenger.${index}.mobile`] = el)
                        }
                        value={passenger.mobile}
                        onChange={(e) =>
                          handlePassengerChange(index, "mobile", e.target.value)
                        }
                        placeholder="Enter Mobile Number"
                        bg="white"
                        maxLength={10}
                        isReadOnly={leadId || (index === 0 && firstPassengerSameAsCustomer)}
                        opacity={(leadId || (index === 0 && firstPassengerSameAsCustomer)) ? 0.7 : 1}
                        bgColor={(leadId || (index === 0 && firstPassengerSameAsCustomer)) ? "gray.100" : "white"}
                        cursor={(leadId || (index === 0 && firstPassengerSameAsCustomer)) ? "not-allowed" : "auto"}
                      />
                      {passengerErrors[index]?.mobile && (
                        <FormErrorMessage>
                          {passengerErrors[index].mobile}
                        </FormErrorMessage>
                      )}
                    </FormControl>

                    <FormControl isInvalid={passengerErrors[index]?.whatsapp}>
                      <FormLabel>
                        Whatsapp Number <span style={{ color: "red" }}>*</span>
                      </FormLabel>
                      <Input
                        ref={(el) =>
                          (fieldRefs.current[`passenger.${index}.whatsapp`] = el)
                        }
                        value={passenger.whatsapp}
                        onChange={(e) =>
                          handlePassengerChange(index, "whatsapp", e.target.value)
                        }
                        placeholder="Enter Whatsapp Number"
                        bg="white"
                        maxLength={10}
                        isReadOnly={leadId || passengerWhatsappSameAsMobile[index] || (index === 0 && firstPassengerSameAsCustomer)}
                        opacity={(leadId || passengerWhatsappSameAsMobile[index] || (index === 0 && firstPassengerSameAsCustomer)) ? 0.7 : 1}
                        bgColor={(leadId || passengerWhatsappSameAsMobile[index] || (index === 0 && firstPassengerSameAsCustomer)) ? "gray.100" : "white"}
                        cursor={(leadId || passengerWhatsappSameAsMobile[index] || (index === 0 && firstPassengerSameAsCustomer)) ? "not-allowed" : "auto"}
                      />
                      {passengerErrors[index]?.whatsapp && (
                        <FormErrorMessage>
                          {passengerErrors[index].whatsapp}
                        </FormErrorMessage>
                      )}
                      <Checkbox
                        mt={2}
                        size="sm"
                        isChecked={passengerWhatsappSameAsMobile[index]}
                        onChange={(e) => {
                          const newStates = [...passengerWhatsappSameAsMobile];
                          newStates[index] = e.target.checked;
                          setPassengerWhatsappSameAsMobile(newStates);
                          if (e.target.checked) {
                            handlePassengerChange(
                              index,
                              "whatsapp",
                              normalizeIndianMobile(passenger.mobile),
                            );
                          }
                        }}
                        isDisabled={leadId}
                      >
                        Same as mobile number
                      </Checkbox>
                    </FormControl>
                  </Grid>
                </Box>
              ))}
          </Box>
        </VStack>

        {/* RIGHT COLUMN - Booking Summary */}
        <VStack align="stretch" spacing={4}>
          {/* Price Summary Card */}
          <Box
            bg="white"
            p={5}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <Text fontWeight="600" mb={4}>
              {isCustomBooking ? "Custom Booking Charge" : "Booking Charge"}
            </Text>

            <Stack spacing={3} fontSize="sm">
              {isCustomBooking && customFare ? (
                <>
                  {/* Custom Booking Fare Display */}
                  {customFare.pricingOutputs?.breakdown ? (
                    <>
                      {/* Mileage-based pricing breakdown */}
                      <Flex justify="space-between">
                        <Text color="#6B7280">Base Fare</Text>
                        <Text color="#6B7280">
                          ₹
                          {(customFare.pricingOutputs.totalFare -
                            (customFare.pricingOutputs.breakdown.isTollIncludedInPrice ? (customFare.pricingOutputs.breakdown.tollTax || 0) : 0) -
                            (customFare?.pricingOutputs?.breakdown?.driverAllowance || 0) -
                            customFare.pricingOutputs.breakdown.gstAmount).toFixed(2)}
                        </Text>
                      </Flex>

                      {customFare.pricingOutputs.breakdown.driverAllowance > 0 && (
                        <Flex justify="space-between">
                          <Text color="#6B7280" fontSize="sm">
                            Driver Allowance
                          </Text>
                          <Text color="#6B7280" fontSize="sm">
                            ₹{customFare.pricingOutputs.breakdown.driverAllowance.toFixed(2)}
                          </Text>
                        </Flex>
                      )}

                      <Divider />

                      {customFare.pricingOutputs.breakdown.gstAmount > 0 && (
                        <Flex justify="space-between">
                          <Text color="#6B7280" fontSize="sm">
                            GST (18%)
                          </Text>
                          <Text color="#6B7280" fontSize="sm">
                            ₹{customFare.pricingOutputs.breakdown.gstAmount.toFixed(2)}
                          </Text>
                        </Flex>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Fixed pricing display */}
                      <Flex justify="space-between">
                        <Text color="#6B7280">Base Fare</Text>
                        <Text color="#6B7280">
                          ₹
                          {(customFare.pricingOutputs?.totalFare ||
                            formData.fixedRate)?.toFixed?.(2) || (customFare.pricingOutputs?.totalFare || formData.fixedRate)}
                        </Text>
                      </Flex>
                    </>
                  )}

                  <Flex justify="space-between" fontWeight="600">
                    <Text>Total Charges</Text>
                    <Text>₹{customFare.pricingOutputs?.totalFare?.toFixed(2)}</Text>
                  </Flex>
                </>
              ) : (
                <>
                  {/* Regular booking fare display */}
                  <Flex justify="space-between">
                    <Text color="#6B7280">Base Fare</Text>
                    <Text color="#6B7280">
                      ₹
                      {fareBreakup?.baseFare?.toFixed(2)}
                    </Text>
                  </Flex>

                  {fareBreakup?.driverAllowance > 0 && (
                    <Flex justify="space-between">
                      <Text color="#6B7280" fontSize="sm">
                        Driver Allowance
                      </Text>
                      <Text color="#6B7280" fontSize="sm">
                        ₹{fareBreakup?.driverAllowance.toFixed(2)}
                      </Text>
                    </Flex>
                  )}

                  {fareBreakup?.tollTax > 0 && fareBreakup?.isTollIncludedInPrice && (
                    <Flex justify="space-between">
                      <Text color="#6B7280" fontSize="sm">
                        (Estimated) Toll Tax
                      </Text>
                      <Text color="#6B7280" fontSize="sm">
                        ₹{fareBreakup?.tollTax.toFixed(2)}
                      </Text>
                    </Flex>
                  )}

                  {/* premium */}
                  {fareBreakup?.premiumAmount > 0 && (
                    <Flex justify="space-between">
                      <Text color="#6B7280" fontSize="sm">
                        Premium Amount
                      </Text>
                      <Text color="#6B7280" fontSize="sm">
                        ₹{fareBreakup?.premiumAmount.toFixed(2)}
                      </Text>
                    </Flex>
                  )}

                  {/* surcharge */}
                  {fareBreakup?.surchargeAmount > 0 && (
                    <Flex justify="space-between">
                      <Text color="#6B7280" fontSize="sm">
                        Surcharge Amount
                      </Text>
                      <Text color="#6B7280" fontSize="sm">
                        ₹{fareBreakup?.surchargeAmount.toFixed(2)}
                      </Text>
                    </Flex>
                  )}

                  <Divider />

                  <Flex justify="space-between">
                    <Text color="#6B7280" fontSize="sm">
                      GST (18%)
                    </Text>
                    <Text color="#6B7280" fontSize="sm">
                      ₹{fareBreakup?.gstOnCommission.toFixed(2)}
                    </Text>
                  </Flex>

                  {fareBreakup?.discountAmount > 0 && (
                    <Flex justify="space-between">
                      <Text color="#6B7280" fontSize="sm">
                        Discount Amount
                      </Text>
                      <Text color="#6B7280" fontSize="sm">
                        -₹{fareBreakup?.discountAmount.toFixed(2)}
                      </Text>
                    </Flex>
                  )}

                  <Flex justify="space-between" fontWeight="600">
                    <Text>Total Charges</Text>
                    <Text>₹{Number(fareBreakup?.customerPayable || 0).toFixed(2)}</Text>
                  </Flex>
                </>
              )}

              <Divider />

              <Flex justify={"space-between"}>
                <Text fontWeight="700" fontSize="sm">
                  Discount
                </Text>
                <InputGroup size="sm" maxW="140px">
                  <InputLeftAddon>₹</InputLeftAddon>
                  <Input
                    size="sm"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={manualDiscount === 0 ? '' : manualDiscount}
                    isDisabled={!!leadId}
                    onChange={(e) => {
                      let cleaned = e.target.value.replace(/[^0-9.]/g, "");
                      // Prevent multiple decimal points
                      const parts = cleaned.split('.');
                      if (parts.length > 2) {
                        cleaned = parts[0] + '.' + parts.slice(1).join('');
                      }
                      if (cleaned === '' || cleaned === '.') {
                        setManualDiscount(0);
                      } else {
                        setManualDiscount(cleaned);
                      }
                    }}
                    onWheel={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      return false;
                    }}
                  />
                </InputGroup>
              </Flex>
              {isOverDiscount && (
                <Text fontSize="xs" color="red.500">
                  Discount cannot be greater than the system calculated fare.
                </Text>
              )}

              {manualDiscount > 0 && (
                <Flex justify="space-between" fontWeight="600" mt={2}>
                  <Text>Final Price</Text>
                  <Text>₹{finalPrice.toFixed(2)}</Text>
                </Flex>
              )}

              <Divider />

              <Flex justify="space-between" fontWeight="700" fontSize="lg">
                <Text>Total</Text>
                <Text color="#1E66FF">₹{finalPrice.toFixed(2)}</Text>
              </Flex>

              <Divider />

              <Checkbox
                mt={2}
                isChecked={sendWhatsappMsg}
                isDisabled={!!leadId}
                onChange={(e) => {
                  if (!leadId) {
                    setSendWhatsappMsg(e.target.checked);
                  }
                }}
              >
                <Text fontSize="sm">Send Details on Whatsapp</Text>
              </Checkbox>


              <Button
                colorScheme={leadId ? "green" : "blue"}
                w="100%"
                onClick={handleAddLead}
                variant={leadId ? "solid" : "solid"}
                isDisabled={!!leadId || isOverDiscount}
                cursor={leadId || isOverDiscount ? "not-allowed" : "pointer"}
                _disabled={{
                  opacity: 1,
                  cursor: "not-allowed",
                  bg: leadId ? "green.500" : undefined,
                  color: leadId ? "white" : undefined,
                  _hover: leadId ? { bg: "green.600" } : undefined,
                }}
              >
                {leadId
                  ? "Lead Already Added ✓"
                  : isCustomBooking
                    ? "Add Custom Booking & Lead"
                    : "Add Lead"}
              </Button>
            </Stack>
          </Box>

          {/* Token box: placed below the price summary inside the right column */}
          <Box
            bg="white"
            p={4}
            mt={4}
            rounded="md"
            border="1px solid #E8EDF7"
            boxShadow="sm"
          >
            <Text fontWeight="600" mb={3}>
              Token
            </Text>

            <Stack spacing={3}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm">
                  Token Amount
                  {!leadId && (
                    <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                      (Available after lead is added)
                    </Text>
                  )}
                </Text>

                <InputGroup size="sm" maxW="140px">
                  <InputLeftAddon>₹</InputLeftAddon>
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={tokenAmount === 0 ? '' : tokenAmount}
                    isDisabled={!leadId}
                    ref={tokenRef}
                    onChange={(e) => {
                      let cleaned = e.target.value.replace(/[^0-9.]/g, "");
                      // Prevent multiple decimal points
                      const parts = cleaned.split('.');
                      if (parts.length > 2) {
                        cleaned = parts[0] + '.' + parts.slice(1).join('');
                      }
                      if (cleaned === '' || cleaned === '.') {
                        setTokenAmount(0);
                      } else {
                        setTokenAmount(cleaned);
                      }
                    }}
                    onWheel={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                </InputGroup>
              </Flex>

              {isTokenOverFinalPrice && (
                <Text fontSize="xs" color="red.500">
                  Token amount cannot be greater than the final price.
                </Text>
              )}

              <Button
                colorScheme="green"
                w="100%"
                onClick={handleSendToken}
                isDisabled={!leadId || isOverDiscount || isTokenOverFinalPrice}
              >
                Send Token Request
              </Button>
            </Stack>
          </Box>

          {/* Inclusions and Exclusions */}
          {!isCustomBooking && (() => {
            const { includes, excludes } = getPricingInclusions();

            if (includes.length === 0 && excludes.length === 0) return null;

            return (
              <Box
                bg="white"
                p={5}
                rounded="md"
                border="1px solid #E8EDF7"
                boxShadow="sm"
              >
                <Text fontWeight="600" mb={4}>
                  Trip Details
                </Text>
                <Flex gap={6} direction={{ base: "column", md: "column" }}>
                  {includes.length > 0 && (
                    <Box>
                      <Text
                        fontWeight="600"
                        mb={3}
                        color="#10B981"
                        fontSize="sm"
                      >
                        ✓ Inclusions
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {includes.map((label, index) => (
                          <Flex key={index} align="center" gap={2}>
                            <Box
                              w="6px"
                              h="6px"
                              borderRadius="full"
                              bg="#10B981"
                            />
                            <Text fontSize="sm" color="#6B7280">
                              {label}
                            </Text>
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {excludes.length > 0 && (
                    <Box mt={includes.length > 0 ? 4 : 0}>
                      <Text
                        fontWeight="600"
                        mb={3}
                        color="#EF4444"
                        fontSize="sm"
                      >
                        ✗ Exclusions
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {excludes.map((label, index) => (
                          <Flex key={index} align="center" gap={2}>
                            <Box
                              w="6px"
                              h="6px"
                              borderRadius="full"
                              bg="#EF4444"
                            />
                            <Text fontSize="sm" color="#6B7280">
                              {label}
                            </Text>
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Flex>
              </Box>
            );
          })()}

          {(() => {
            const terms = Array.isArray(vehicle?.termsAndConditions)
              ? vehicle.termsAndConditions
              : [];

            return (
              <Box
                bg="white"
                p={5}
                rounded="md"
                border="1px solid #E8EDF7"
                boxShadow="sm"
              >
                <Button
                  variant="unstyled"
                  w="100%"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  textAlign="left"
                  onClick={() => setIsTermsOpen(!isTermsOpen)}
                  aria-expanded={isTermsOpen}
                  aria-controls="terms-and-conditions-content"
                  isDisabled={!terms.length}
                  _disabled={{ cursor: "not-allowed", opacity: 1 }}
                >
                  <Text fontWeight="600" color="#1A202C" fontSize="md">
                    Terms and Conditions
                  </Text>

                  {terms.length > 0 && (
                    <Flex align="center" gap={1}>
                      <Text fontSize="sm" color="blue.500">
                        {isTermsOpen ? "Hide" : "Show"}
                      </Text>
                      {isTermsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </Flex>
                  )}
                </Button>

                {terms.length > 0 ? (
                  <Collapse in={isTermsOpen} animateOpacity>
                    <Box id="terms-and-conditions-content">
                      <VStack align="stretch" spacing={3} mt={4}>
                        {terms.map((label, index) => (
                          <Flex key={index} align="flex-start" gap={3}>
                            <Box
                              mt="6px"
                              minW="8px"
                              w="8px"
                              h="8px"
                              borderRadius="full"
                              bg="#3B82F6"
                            />
                            <Text
                              fontSize="sm"
                              color="#4B5563"
                              lineHeight="1.6"
                            >
                              {label}
                            </Text>
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                  </Collapse>
                ) : (
                  <Text fontSize="sm" color="#6B7280" mt={3}>
                    No terms and conditions available.
                  </Text>
                )}
              </Box>
            );
          })()}
        </VStack>
      </Grid>
    </Box >
  );
}